import { PaginatedActivities, Activity, PaginatedNotifications, NotificationPreferences, ActivityReviewsResponse, ActivityReview, ActivityReviewRequest } from '../types/Activity';
import { fetchMockActivities } from './mockData';
import { auth } from '../config/firebase';

// User-related interfaces based on backend documentation
// Subscription-related interfaces
export interface SubscriptionPlan {
  id: number;
  name: string;
  display_name: string;
  description: string;
  price: number;
  billing_cycle: 'monthly' | 'yearly';
  activity_limit: number;
  features: string[];
  is_active: boolean;
  created_at: string;
}

// New unified subscription structure
export interface Subscription {
  type: 'trial' | 'subscription' | 'none' | 'team_member' | 'global_free_access';
  status: 'active' | 'expired';
  can_create_activity: boolean;
  activities_remaining: number;
  credit_balance: number | null;
  reasons_blocked: string[];
  
  trial: {
    is_active: boolean;
    end_date: string;
    days_remaining: number;
  } | null;
  
  plan: SubscriptionPlan | null;
  
  billing: {
    next_charge_date: string;
    amount: number;
    currency: string;
    cycle: string;
  } | null;
  
  usage: {
    activities_created_this_period: number;
    period_start: string | null;
  };
  // Optional expiry for global free access periods or subscriptions
  expires_at?: string | null;
}

export interface GalleryMediaItem {
  id: number;
  image_url: string;
  caption?: string | null;
  is_public: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface PaginatedGalleryResponse {
  limit: number;
  count: number;
  results: GalleryMediaItem[];
}

export interface CreateGalleryMediaRequest {
  image_url: string;
  caption?: string | null;
  is_public?: boolean;
  display_order?: number;
}

export interface UpdateGalleryMediaRequest {
  caption?: string | null;
  is_public?: boolean;
  display_order?: number;
}

export type UserGender = 'female' | 'male' | 'non_binary' | 'other' | 'prefer_not_to_say';

export interface UserProfile {
  userId: string;
  name: string;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  gender?: UserGender | null;
  birthday?: string | null;
  profilePictureUrl?: string;
  bio?: string;
  interests?: number[];
  interests_detail?: Array<{ id: number; name: string; description: string }>;
  preferred_tags?: number[];
  preferred_tags_detail?: Array<{ id: number; name: string; created_at: string }>;
  city?: string;
  country?: string;
  
  // New unified subscription structure
  subscription: Subscription;
  // Backend-provided flag to grant unlimited access for team members
  is_team_member?: boolean;
  
  // Terms & Conditions
  terms_accepted: boolean;
  terms_accepted_at: string | null;
  terms_version: string | null;
  has_accepted_current_terms: boolean;

  // Gallery data
  gallery?: GalleryMediaItem[];
  galleryPreview?: GalleryMediaItem[];
  galleryCount?: number;
  galleryPrivateCount?: number;
  galleryLimit?: number;
}

export interface UserProfileUpdate {
  first_name?: string | null;
  last_name?: string | null;
  gender?: UserGender | '' | null;
  birthday?: string | null;
  bio?: string;
  profile_picture_url?: string;
  interests?: number[];
  preferred_tags?: number[];
  city?: string;
  country?: string;
}

// Account deletion interfaces
export interface AccountDeletionSummary {
  user_id: string;
  user_email: string;
  deleted_at: string;
  data_deleted: {
    activities_created: number;
    activity_participations: number;
    activity_media_uploaded: number;
    invitations_sent: number;
    invitations_received: number;
    notifications: number;
    ratings_given: number;
    ratings_received: number;
    groups_created: number;
    group_memberships: number;
    organizations_owned: number;
    device_tokens: number;
    push_notifications: number;
    subscription_history: number;
    chat_conversations_participant: number;
    chat_conversations_organizer: number;
    chat_messages: number;
    ai_searches: number;
    ai_conversations: number;
    ai_recommendations: number;
    ai_feedback: number;
    ai_insights: number;
    total_items: number;
  };
}

export interface AccountDeletionResponse {
  success: boolean;
  message: string;
  deletion_summary: AccountDeletionSummary;
  note: string;
}

// Terms & Conditions interfaces
export interface TermsAndConditions {
  id: number;
  language: 'EN' | 'DE';
  version: string;
  title: string;
  content: string;
  effective_date: string;
  is_active: boolean;
  created_at: string;
}

// Auth provider discovery
export interface ProvidersResponse {
  google: boolean;
  email_password?: boolean;
}

export const fetchAvailableAuthProviders = async (): Promise<ProvidersResponse> => {
  const headers = await getDualAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/auth/providers/`, {
    method: 'GET',
    headers,
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
};

export interface TermsAcceptance {
  user_id: string;
  terms_version: string;
  terms_accepted_at: string;
  language: 'EN' | 'DE';
}

export interface TermsStatus {
  user_id: string;
  terms_accepted: boolean;
  terms_accepted_at?: string;
  terms_version?: string;
  current_version: string;
  has_accepted_current_terms: boolean;
  requires_new_acceptance: boolean;
}

export interface OrganizerStats {
  userId: string;
  name: string;
  username: string;
  email: string;
  gender?: UserGender | null;
  birthday?: string | null;
  age?: number | null;
  profilePictureUrl?: string;
  bio?: string;
  city?: string;
  country?: string;
  created_activities_count: number;
  participations_count: number;
  groups_count: number;
  given_ratings_count: number;
  received_ratings_count: number;
  average_received_rating: number;
  recent_created_activities: Array<{
    id: number;
    title: string;
    date: string;
    time: string;
    location_name: string;
    city: string;
    current_participants: number;
    max_participants: number;
  }>;
  recent_participations: Array<{
    activity: {
      id: number;
      title: string;
      date: string;
      time: string;
      location_name: string;
      city: string;
    };
    status: string;
    registered_at: string;
  }>;
  groups: Array<{
    id: number;
    name: string;
    description?: string;
    member_count: number;
    created_at: string;
  }>;
  gallery?: GalleryMediaItem[];
  galleryPreview?: GalleryMediaItem[];
  galleryCount?: number;
  galleryPrivateCount?: number;
  galleryLimit?: number;
}

export interface Category {
  id: number;
  name: string;
  description: string;
  created_at?: string;
  updated_at?: string;
  count?: number; // Activity count for this category (from backend)
}

export interface Organizer {
  userId: string;
  id: string;
  name: string;
  username: string;
  email: string;
  profilePictureUrl?: string;
  bio?: string;
  city?: string;
  country?: string;
  activities_count: number;
  average_rating: number;
  total_ratings: number;
  recent_activities_30_days: number;
  created_at: string;
}

export interface OrganizersResponse {
  organizers: Organizer[];
  total_organizers: number;
}

export interface PublicMemberInterest {
  id: number;
  name: string;
  description?: string;
}

export interface PublicMemberActivity {
  id: number;
  title: string;
  date: string;
  time?: string;
  city?: string;
  country?: string;
  location_name?: string;
  cover_image?: string | null;
}

const normalizeGenderValue = (value: unknown): UserGender | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.toLowerCase().trim();
  const validGenders: UserGender[] = [
    'female',
    'male',
    'non_binary',
    'other',
    'prefer_not_to_say',
  ];

  return (validGenders as string[]).includes(normalized) ? (normalized as UserGender) : null;
};

export interface PublicMemberSummary {
  userId: string;
  id?: string;
  name: string;
  username?: string;
  profilePictureUrl?: string | null;
  bio?: string | null;
  city?: string | null;
  country?: string | null;
  isOrganizer?: boolean;
  interests?: PublicMemberInterest[];
  interests_detail?: PublicMemberInterest[];
  gallery?: GalleryMediaItem[];
  galleryPreview?: GalleryMediaItem[];
  galleryCount?: number;
  galleryPrivateCount?: number;
  galleryLimit?: number;
  activities?: PublicMemberActivity[];
  createdActivities?: PublicMemberActivity[];
  gender?: UserGender | null;
  birthday?: string | null;
  /** Social discovery (backend member cards / suggested feed) */
  liked_by_me?: boolean;
  likes_me?: boolean;
  shared_events_count?: number;
  /** Event participations (when provided by profile / discovery APIs) */
  participations_count?: number;
  badge_count?: number;
  match_id?: string | null;
  matched_at?: string | null;
  liked_at?: string | null;
}

export interface PublicMembersResponse {
  members: PublicMemberSummary[];
  total_members?: number;
  count?: number;
  next?: string | null;
  previous?: string | null;
}

export interface SearchResponse {
  search_term: string;
  results_count: number;
  activities: Activity[];
}

export interface SearchParams {
  q: string;
  category?: number;
  min_lat?: number;
  max_lat?: number;
  min_lon?: number;
  max_lon?: number;
}

export interface MapBounds {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://connectandgo-backend-505896990567.europe-west1.run.app';
const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA === 'true';

// Helper function to get authenticated headers
// Token cache and rate limiting
interface TokenCache {
  token: string;
  expiresAt: number;
  userId: string;
  issuedAt: number;
}

let tokenCache: TokenCache | null = null;
let lastTokenRequest = 0;
let tokenRequestCount = 0;
let backoffUntil = 0;
let circuitBreakerFailures = 0;
let circuitBreakerOpenUntil = 0;
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const MIN_TOKEN_REQUEST_INTERVAL = 1000; // 1 second between requests
const MAX_REQUESTS_PER_MINUTE = 10; // Increased from 5 to 10 for better UX
const BACKOFF_INITIAL_DELAY = 30000; // 30 seconds (reduced from 60s for better UX)
const BACKOFF_MAX_DELAY = 300000; // 5 minutes (reduced from 10 for better UX)
const BACKOFF_MULTIPLIER = 2; // Less aggressive backoff
const CIRCUIT_BREAKER_THRESHOLD = 5; // Trip circuit breaker after 5 failures (increased from 3)
const CIRCUIT_BREAKER_TIMEOUT = 300000; // 5 minutes (reduced from 10)
const TOKEN_EXPIRY_BUFFER = 300000; // 5 minutes before actual expiry
const TOKEN_LIFETIME = 3600000; // 1 hour (Firebase default)

const resetTokenRequestCount = () => {
  setTimeout(() => {
    tokenRequestCount = 0;
  }, 60000); // Reset every minute
};

/**
 * Check if token is expired or about to expire
 */
const isTokenExpired = (cache: TokenCache | null): boolean => {
  if (!cache) return true;
  const now = Date.now();
  // Check if token is past expiry time (including buffer)
  return now >= cache.expiresAt;
};

/**
 * Check if token needs refresh (within expiry buffer window)
 */
const shouldRefreshToken = (cache: TokenCache | null): boolean => {
  if (!cache) return true;
  const now = Date.now();
  // Refresh if we're within the buffer window before expiry
  return now >= (cache.expiresAt - TOKEN_EXPIRY_BUFFER / 2);
};

/**
 * Subscribe to token refresh completion
 */
const subscribeTokenRefresh = (callback: (token: string) => void): void => {
  refreshSubscribers.push(callback);
};

/**
 * Notify all subscribers when token refresh completes
 */
const onRefreshComplete = (token: string): void => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

/**
 * Refresh Firebase token - centralized function
 * Handles concurrent refresh requests using a subscription pattern
 */
const refreshFirebaseToken = async (forceRefresh: boolean = false): Promise<string> => {
  if (!auth.currentUser) {
    throw new Error('No authenticated user');
  }

  // If already refreshing, wait for the refresh to complete
  if (isRefreshing) {
    console.log('🔄 Token refresh in progress, waiting...');
    return new Promise((resolve) => {
      subscribeTokenRefresh((token) => {
        resolve(token);
      });
    });
  }

  isRefreshing = true;
  const now = Date.now();

  try {
    console.log(`🔑 ${forceRefresh ? 'Force refreshing' : 'Refreshing'} Firebase token...`);
    
    // Track the request for rate limiting
    lastTokenRequest = now;
    tokenRequestCount++;
    
    // Get fresh token - force refresh if requested or if token is expired
    const shouldForce = forceRefresh || isTokenExpired(tokenCache);
    const idToken = await auth.currentUser.getIdToken(shouldForce);
    
    // Cache the token with proper expiry
    tokenCache = {
      token: idToken,
      expiresAt: now + TOKEN_LIFETIME - TOKEN_EXPIRY_BUFFER,
      userId: auth.currentUser.uid,
      issuedAt: now
    };
    
    // Reset circuit breaker on successful request
    circuitBreakerFailures = 0;
    console.log('✅ Firebase token refreshed successfully');
    
    // Notify all subscribers
    onRefreshComplete(idToken);
    
    return idToken;
  } catch (error: unknown) {
    console.error('🚨 Error refreshing Firebase token:', error);
    
    // Increment circuit breaker failure count
    circuitBreakerFailures++;
    
    // Handle quota exceeded specifically
    if ((error as { code?: string }).code === 'auth/quota-exceeded') {
      console.error(`🔥 Firebase quota exceeded (failure ${circuitBreakerFailures}/${CIRCUIT_BREAKER_THRESHOLD})`);
      
      // Open circuit breaker if too many failures
      if (circuitBreakerFailures >= CIRCUIT_BREAKER_THRESHOLD) {
        circuitBreakerOpenUntil = now + CIRCUIT_BREAKER_TIMEOUT;
        console.error(`🚨 Circuit breaker OPENED - no Firebase requests for ${CIRCUIT_BREAKER_TIMEOUT/60000} minutes`);
      }
      
      // Calculate exponential backoff
      const backoffMultiplier = Math.min(circuitBreakerFailures, 5);
      const currentBackoff = Math.min(
        BACKOFF_INITIAL_DELAY * Math.pow(BACKOFF_MULTIPLIER, backoffMultiplier),
        BACKOFF_MAX_DELAY
      );
      
      backoffUntil = now + currentBackoff;
      console.error(`⏰ Backoff active: ${Math.round(currentBackoff/1000)}s`);
    }
    
    throw error;
  } finally {
    isRefreshing = false;
  }
};

export const getAuthHeaders = async (): Promise<HeadersInit> => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-API-KEY': import.meta.env.VITE_API_KEY || '',
  };

  // Add Firebase ID token if user is authenticated
  if (auth.currentUser) {
    const now = Date.now();

    // Circuit breaker check - if open, use cached token if available
    if (now < circuitBreakerOpenUntil) {
      console.warn(`🚨 Circuit breaker OPEN - blocked until ${new Date(circuitBreakerOpenUntil).toLocaleTimeString()}`);
      if (tokenCache && tokenCache.userId === auth.currentUser.uid && !isTokenExpired(tokenCache)) {
        headers['Authorization'] = `Bearer ${tokenCache.token}`;
        return headers;
      }
      console.warn('⚠️ No valid cached token available - API calls may fail');
      return headers;
    }

    try {
      // Check if we're in backoff period
      if (now < backoffUntil) {
        console.warn(`⏳ Token requests in backoff until ${new Date(backoffUntil).toLocaleTimeString()}`);
        // Use cached token if available and not expired
        if (tokenCache && tokenCache.userId === auth.currentUser.uid && !isTokenExpired(tokenCache)) {
          headers['Authorization'] = `Bearer ${tokenCache.token}`;
          return headers;
        }
        return headers;
      }

      // Rate limiting check
      if (tokenRequestCount >= MAX_REQUESTS_PER_MINUTE) {
        console.warn(`🚫 Rate limit: ${tokenRequestCount}/${MAX_REQUESTS_PER_MINUTE} requests this minute`);
        if (tokenCache && tokenCache.userId === auth.currentUser.uid && !isTokenExpired(tokenCache)) {
          headers['Authorization'] = `Bearer ${tokenCache.token}`;
          return headers;
        }
        // Set a mini-backoff for rate limit exceeded
        backoffUntil = now + 15000; // 15 seconds
        return headers;
      }

      // Minimum interval check
      if (now - lastTokenRequest < MIN_TOKEN_REQUEST_INTERVAL && !isRefreshing) {
        if (tokenCache && tokenCache.userId === auth.currentUser.uid && !isTokenExpired(tokenCache)) {
          headers['Authorization'] = `Bearer ${tokenCache.token}`;
          return headers;
        }
      }

      // Check if we have a valid cached token
      if (tokenCache && 
          tokenCache.userId === auth.currentUser.uid && 
          !isTokenExpired(tokenCache)) {
        headers['Authorization'] = `Bearer ${tokenCache.token}`;
        
        // Proactively refresh if token will expire soon (but don't block)
        if (shouldRefreshToken(tokenCache)) {
          console.log('🔄 Token expiring soon, refreshing in background...');
          refreshFirebaseToken(false).catch(err => {
            console.warn('⚠️ Background token refresh failed:', err);
          });
        }
        
        return headers;
      }

      // Token is expired or doesn't exist - get a fresh one
      console.log('🔑 Token expired or missing, getting fresh token...');
      const idToken = await refreshFirebaseToken(true);
      headers['Authorization'] = `Bearer ${idToken}`;
      
    } catch (error: unknown) {
      console.error('🚨 Error getting Firebase ID token:', error);
      
      // Try to use cached token as fallback, even if expired
      if (tokenCache && tokenCache.userId === auth.currentUser?.uid) {
        console.warn('🆘 Using cached token as fallback (may be expired)');
        headers['Authorization'] = `Bearer ${tokenCache.token}`;
      }
    }
  }

  return headers;
};

/**
 * Enhanced fetch wrapper with automatic 401 error handling and retry logic
 * This wrapper intercepts 401 errors, refreshes the token, and retries the request
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  // Get initial headers
  const headers = await getAuthHeaders();
  const mergedHeaders = {
    ...headers,
    ...options.headers,
  };

  // Make the first request
  let response = await fetch(url, {
    ...options,
    headers: mergedHeaders,
  });

  // If we get a 401 Unauthorized error, try to refresh token and retry
  if (response.status === 401) {
    console.warn('🔐 Received 401 Unauthorized - attempting token refresh and retry');
    
    try {
      // Force refresh the Firebase token
      await refreshFirebaseToken(true);
      
      // Get fresh headers with new token
      const freshHeaders = await getAuthHeaders();
      const retryHeaders = {
        ...freshHeaders,
        ...options.headers,
      };
      
      console.log('🔄 Retrying request with refreshed token...');
      
      // Retry the request with fresh token
      response = await fetch(url, {
        ...options,
        headers: retryHeaders,
      });
      
      if (response.ok) {
        console.log('✅ Request succeeded after token refresh');
      } else if (response.status === 401) {
        console.error('🚨 Still getting 401 after token refresh - authentication issue');
      }
    } catch (refreshError) {
      console.error('🚨 Failed to refresh token after 401:', refreshError);
      // Return the original 401 response if refresh fails
    }
  }

  return response;
};

/**
 * Simplified fetch wrapper for authenticated requests that also handles errors
 * Returns parsed JSON data or throws an error
 */
export const authenticatedFetchJSON = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await authenticatedFetch(url, options);
  
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
  }
  
  return response.json();
};

/**
 * Get authentication headers for Firebase auth
 * This function gets the Firebase ID token for backend API calls
 */
export const getDualAuthHeaders = async (): Promise<HeadersInit> => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-API-KEY': import.meta.env.VITE_API_KEY || '',
  };

  try {
    // Check Firebase auth
    if (auth.currentUser) {
      console.log('🔑 Using Firebase authentication');
      
      try {
        const idToken = await auth.currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${idToken}`;
        headers['X-Auth-Provider'] = 'firebase';
        return headers;
      } catch (error) {
        console.error('❌ Firebase token error:', error);
      }
    }

    // No authentication found
    console.log('⚠️ No authentication found - using headers without auth');
    return headers;

  } catch (error) {
    console.error('❌ Error getting auth headers:', error);
    return headers;
  }
};

// Reset token request count every minute
resetTokenRequestCount();

// Utility functions for debugging and manual cache management
export const clearTokenCache = () => {
  console.log('🧹 Clearing Firebase token cache and resetting all state');
  tokenCache = null;
  backoffUntil = 0;
  tokenRequestCount = 0;
  lastTokenRequest = 0;
  circuitBreakerFailures = 0;
  circuitBreakerOpenUntil = 0;
  isRefreshing = false;
  refreshSubscribers = [];
};

export const getTokenStatus = () => {
  const now = Date.now();
  return {
    hasCachedToken: !!tokenCache,
    tokenExpiry: tokenCache?.expiresAt,
    tokenIssuedAt: tokenCache?.issuedAt,
    tokenExpiryTime: tokenCache?.expiresAt ? new Date(tokenCache.expiresAt).toLocaleString() : 'No token',
    tokenIssuedTime: tokenCache?.issuedAt ? new Date(tokenCache.issuedAt).toLocaleString() : 'No token',
    tokenAge: tokenCache?.issuedAt ? Math.round((now - tokenCache.issuedAt) / 1000) + 's' : 'N/A',
    tokenTimeUntilExpiry: tokenCache?.expiresAt ? Math.round((tokenCache.expiresAt - now) / 1000) + 's' : 'N/A',
    isTokenExpired: isTokenExpired(tokenCache),
    shouldRefreshToken: shouldRefreshToken(tokenCache),
    isRefreshing,
    requestCount: tokenRequestCount,
    maxRequests: MAX_REQUESTS_PER_MINUTE,
    backoffUntil,
    backoffUntilTime: backoffUntil ? new Date(backoffUntil).toLocaleString() : 'No backoff',
    isInBackoff: now < backoffUntil,
    lastRequest: lastTokenRequest,
    circuitBreakerFailures,
    circuitBreakerThreshold: CIRCUIT_BREAKER_THRESHOLD,
    circuitBreakerOpenUntil,
    circuitBreakerOpenUntilTime: circuitBreakerOpenUntil ? new Date(circuitBreakerOpenUntil).toLocaleString() : 'Closed',
    isCircuitBreakerOpen: now < circuitBreakerOpenUntil,
    emergencyMode: now < backoffUntil || now < circuitBreakerOpenUntil
  };
};

/**
 * Force refresh token - useful for testing or manual token refresh
 */
export const forceTokenRefresh = async (): Promise<void> => {
  console.log('🔄 Manually forcing token refresh...');
  try {
    await refreshFirebaseToken(true);
    console.log('✅ Token force refresh completed');
  } catch (error) {
    console.error('❌ Token force refresh failed:', error);
    throw error;
  }
};

// Helper function to get public headers (no authentication required)
const getPublicHeaders = (): HeadersInit => ({
  'Content-Type': 'application/json',
  'X-API-KEY': import.meta.env.VITE_API_KEY || '',
  // No Authorization header for public calls
});

// =============================================================================
// PUBLIC API FUNCTIONS (No Authentication Required)
// =============================================================================

export interface FeatureFlagsResponse {
  show_backend_data_sections: boolean;
  show_calendar_and_activities_menu: boolean;
  updated_at: string;
}

/**
 * Fetch feature flags to control public experience without redeploys
 */
export const fetchFeatureFlags = async (): Promise<FeatureFlagsResponse> => {
  console.log('🚩 Fetching public feature flags');

  if (USE_MOCK_DATA) {
    return {
      show_backend_data_sections: false,
      show_calendar_and_activities_menu: false,
      updated_at: new Date().toISOString(),
    };
  }

  try {
    const url = `${API_BASE_URL}/api/feature-flags/`;
    const headers = getPublicHeaders();
    const response = await fetch(url, { headers });

    console.log('🚩 Feature flag response:', {
      status: response.status,
      ok: response.ok,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }

    const data: FeatureFlagsResponse = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Failed to fetch feature flags:', error);
    throw error;
  }
};

/**
 * Fetch public activities - accessible without authentication
 * For SEO and public browsing experience
 */
export const fetchPublicActivities = async (page: number = 1, city?: string, country?: string, category?: number): Promise<PaginatedActivities> => {
  console.log('🔍 fetchPublicActivities called with:', {
    page,
    city,
    country,
    category,
    isPublic: true
  });

  // Use mock data if enabled
  if (USE_MOCK_DATA) {
    console.log('📝 Using mock data (VITE_USE_MOCK_DATA=true)');
    return fetchMockActivities(page);
  }

  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    if (city) {
      params.append('city', city);
      console.log('🏙️ Public filtering by city:', city);
    }
    if (country) {
      params.append('country', country);
      console.log('🌍 Public filtering by country:', country);
    }
    if (category) {
      params.append('category', category.toString());
      console.log('🏷️ Public filtering by category:', category);
    }
    
    const url = `${API_BASE_URL}/api/activities/?${params.toString()}`;
    console.log('🌐 Public API call to:', url);
    
    const headers = getPublicHeaders();
    const response = await fetch(url, { headers });
    
    console.log('📡 Public API Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: PaginatedActivities = await response.json();
    console.log('✅ Public API Success! Got', data.count, 'activities');
    return data;
  } catch (error) {
    console.error('❌ Public API Error:', error);
    console.log('📝 Falling back to mock data due to error');
    return fetchMockActivities(page);
  }
};

/**
 * Fetch public activity details - accessible without authentication
 * Some fields may be limited for non-authenticated users
 */
export const fetchPublicActivityDetails = async (activityId: number): Promise<Activity> => {
  console.log('🔍 fetchPublicActivityDetails called for ID:', activityId);

  try {
    const url = `${API_BASE_URL}/api/activities/${activityId}/`;
    console.log('🌐 Public API call to:', url);
    
    const headers = getPublicHeaders();
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: Activity = await response.json();
    console.log('✅ Public Activity Details Success!');
    return data;
  } catch (error) {
    console.error('❌ Public Activity Details Error:', error);
    throw error;
  }
};

/**
 * Fetch public categories - accessible without authentication
 */
export const fetchPublicCategories = async (): Promise<Category[]> => {
  console.log('🔍 fetchPublicCategories called');

  try {
    const url = `${API_BASE_URL}/api/activities/categories/`;
    console.log('🌐 Public API call to:', url);
    
    const headers = getPublicHeaders();
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('✅ Public Categories Success! Raw data:', data);
    
    // Handle different response formats
    if (Array.isArray(data)) {
      console.log('📋 Public Categories as direct array, length:', data.length);
      return data;
    } else if (data && Array.isArray(data.results)) {
      console.log('📋 Public Categories as paginated response, length:', data.results.length);
      return data.results;
    } else if (data && Array.isArray(data.categories)) {
      console.log('📋 Public Categories as categories object, length:', data.categories.length);
      return data.categories;
    } else {
      console.warn('⚠️ Unexpected public categories response format:', data);
      return [];
    }
  } catch (error) {
    console.error('❌ Public Categories Error:', error);
    return [];
  }
};

/**
 * Fetch public cities with activities - accessible without authentication
 */
export const fetchPublicCitiesWithActivities = async (country?: string): Promise<CitiesResponse> => {
  console.log('🔍 fetchPublicCitiesWithActivities called for country:', country);

  try {
    // Backend requires a country parameter for the cities endpoint
    const resolvedCountry = country || 'DE';
    const params = new URLSearchParams();
    params.append('country', resolvedCountry);
    
    const url = `${API_BASE_URL}/api/activities/cities/?${params.toString()}`;
    console.log('🌐 Public API call to:', url);
    
    const headers = getPublicHeaders();
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: CitiesResponse = await response.json();
    console.log('✅ Public Cities Success! Got', data.total_cities, 'cities');
    return data;
  } catch (error) {
    console.error('❌ Public Cities Error:', error);
    throw error;
  }
};

/**
 * Fetch public countries with activities - accessible without authentication
 * Optional authentication may show more countries
 */
export const fetchPublicCountriesWithActivities = async (): Promise<CountriesResponse> => {
  console.log('🔍 fetchPublicCountriesWithActivities called');

  try {
    const url = `${API_BASE_URL}/api/activities/countries/`;
    console.log('🌐 Public API call to:', url);
    
    const headers = getPublicHeaders();
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: CountriesResponse = await response.json();
    console.log('✅ Public Countries Success! Got', data.total_countries, 'countries');
    return data;
  } catch (error) {
    console.error('❌ Public Countries Error:', error);
    throw error;
  }
};

/**
 * Search public activities - accessible without authentication
 */
export const searchPublicActivities = async (searchParams: SearchParams | string): Promise<PaginatedActivities> => {
  console.log('🔍 searchPublicActivities called with:', searchParams);

  try {
    let url: string;
    
    if (typeof searchParams === 'string') {
      // Simple string search
      const params = new URLSearchParams();
      params.append('q', searchParams);
      url = `${API_BASE_URL}/api/activities/search/?${params.toString()}`;
    } else {
      // Complex search with parameters
      const params = new URLSearchParams();
      params.append('q', searchParams.q);
      
      if (searchParams.category) {
        params.append('category', searchParams.category.toString());
      }
      if (searchParams.min_lat) {
        params.append('min_lat', searchParams.min_lat.toString());
      }
      if (searchParams.max_lat) {
        params.append('max_lat', searchParams.max_lat.toString());
      }
      if (searchParams.min_lon) {
        params.append('min_lon', searchParams.min_lon.toString());
      }
      if (searchParams.max_lon) {
        params.append('max_lon', searchParams.max_lon.toString());
      }
      
      url = `${API_BASE_URL}/api/activities/search/?${params.toString()}`;
    }
    
    console.log('🌐 Public Search API call to:', url);
    
    const headers = getPublicHeaders();
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: PaginatedActivities = await response.json();
    console.log('✅ Public Search Success! Found', data.count, 'activities');
    return data;
  } catch (error) {
    console.error('❌ Public Search Error:', error);
    throw error;
  }
};

/**
 * Fetch public activities by date range - accessible without authentication
 */
export const fetchPublicActivitiesByDate = async (dateFrom: string, dateTo?: string, city?: string, country?: string, category?: number): Promise<PaginatedActivities> => {
  console.log('🔍 fetchPublicActivitiesByDate called with:', {
    dateFrom,
    dateTo,
    city,
    country,
    category,
    isPublic: true
  });

  try {
    const params = new URLSearchParams();
    // Support both single date and range per backend
    if (dateTo && dateTo !== dateFrom) {
      params.append('dateFrom', dateFrom);
      params.append('dateTo', dateTo);
    } else {
      params.append('date', dateFrom);
    }
    if (city) {
      params.append('city', city);
    }
    if (country) {
      params.append('country', country);
    }
    if (category) {
      params.append('category', category.toString());
    }

    // Use main list endpoint with date params (public allowed)
    const url = `${API_BASE_URL}/api/activities/?${params.toString()}`;
    console.log('🌐 Public API call to:', url);
    
    const headers = getPublicHeaders();
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: PaginatedActivities = await response.json();
    console.log('✅ Public Date-filtered Activities Success!');
    return data;
  } catch (error) {
    console.error('❌ Public Date-filtered Activities Error:', error);
    throw error;
  }
};

/**
 * Fetch public activities by city - accessible without authentication
 */
export const fetchPublicActivitiesByCity = async (city: string, country: string): Promise<CityActivitiesResponse> => {
  console.log('🔍 fetchPublicActivitiesByCity called for:', { city, country });

  try {
    const params = new URLSearchParams();
    params.append('city', city);
    params.append('country', country);
    
    const url = `${API_BASE_URL}/api/activities/by_city/?${params.toString()}`;
    console.log('🌐 Public API call to:', url);
    
    const headers = getPublicHeaders();
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: CityActivitiesResponse = await response.json();
    console.log('✅ Public City Activities Success!');
    return data;
  } catch (error) {
    console.error('❌ Public City Activities Error:', error);
    throw error;
  }
};

/**
 * Fetch public organizers - accessible without authentication
 * Note: This may have limited information compared to authenticated version
 */
export const fetchPublicOrganizers = async (
  search?: string, 
  city?: string, 
  country?: string
): Promise<OrganizersResponse> => {
  console.log('🔍 fetchPublicOrganizers called with:', { search, city, country });

  try {
    const params = new URLSearchParams();
    if (search) {
      params.append('search', search);
    }
    if (city) {
      params.append('city', city);
    }
    if (country) {
      params.append('country', country);
    }
    
    const url = `${API_BASE_URL}/api/users/organizers/?${params.toString()}`;
    console.log('🌐 Public API call to:', url);
    
    const headers = getPublicHeaders();
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: OrganizersResponse = await response.json();
    console.log('✅ Public Organizers Success!');
    return data;
  } catch (error) {
    console.error('❌ Public Organizers Error:', error);
    throw error;
  }
};

export interface FetchPublicMembersParams {
  page?: number;
  search?: string;
  city?: string;
  country?: string;
  organizersOnly?: boolean;
  hasGallery?: boolean;
  excludeMe?: boolean;
}

/**
 * Fetch public members (organizers + community members) with location filters
 */
export const fetchPublicMembers = async ({
  page,
  search,
  city,
  country,
  organizersOnly,
  hasGallery,
  excludeMe,
}: FetchPublicMembersParams = {}): Promise<PublicMembersResponse> => {
  console.log('🔍 fetchPublicMembers called with:', {
    page,
    search,
    city,
    country,
    organizersOnly,
    hasGallery,
    excludeMe,
  });

  try {
    const params = new URLSearchParams();
    if (page && page > 1) {
      params.append('page', page.toString());
    }
    if (search) {
      params.append('search', search);
    }
    if (city) {
      params.append('city', city);
    }
    if (country) {
      params.append('country', country);
    }
    if (organizersOnly) {
      params.append('organizers_only', 'true');
    }
    if (hasGallery) {
      params.append('has_gallery', 'true');
    }
    if (excludeMe) {
      params.append('exclude_me', 'true');
    }

    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/users/${queryString ? `?${queryString}` : ''}`;
    console.log('🌐 Public API call to:', url);

    const headers = await getDualAuthHeaders();
    const response = await fetch(url, { headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('✅ Public Members Success!', {
      count: data?.count ?? data?.total_members ?? (data?.members?.length || 0),
    });

    if (Array.isArray(data?.members)) {
      return data as PublicMembersResponse;
    }

    if (Array.isArray(data?.results)) {
      return {
        members: data.results,
        count: data.count,
        next: data.next,
        previous: data.previous,
      };
    }

    if (Array.isArray(data)) {
      return {
        members: data,
        count: data.length,
      };
    }

    // Unknown shape - ensure we always return consistent structure
    return {
      members: [],
      count: 0,
    };
  } catch (error) {
    console.error('❌ Public Members Error:', error);
    throw error;
  }
};

// ------------------------------
// Social discovery & likes (see docs/social/)
// ------------------------------

export interface LikeUserResponse {
  matched: boolean;
  match_id?: string | null;
}

export interface FetchSuggestedMembersParams {
  page?: number;
  gender?: string;
  min_age?: number;
  max_age?: number;
  interests?: string;
  /** Phase 2: filter to attendees of this activity */
  activity_id?: number;
}

const mapRawToPublicMemberSummary = (raw: unknown): PublicMemberSummary | null => {
  const r = toRecord(raw);
  if (!r) return null;
  const userId = getStringValue(r.user_id ?? r.userId ?? r.uid);
  if (!userId) return null;
  const name =
    getStringValue(r.name ?? r.full_name) ||
    getStringValue(r.username) ||
    'Member';
  return {
    userId,
    id: r.id !== undefined ? String(r.id) : undefined,
    name,
    username: getStringValue(r.username ?? r.handle),
    profilePictureUrl:
      getStringValue(r.profile_picture_url ?? r.profilePictureUrl ?? r.profile_picture) ?? null,
    bio: getStringValue(r.bio) ?? null,
    city: getStringValue(r.city) ?? null,
    country: getStringValue(r.country) ?? null,
    isOrganizer: Boolean(
      getBooleanValue(r.is_organizer) ?? getBooleanValue(r.isOrganizer),
    ),
    interests: getInterestsArray(r.interests),
    interests_detail: getInterestsArray(r.interests_detail ?? r.interestsDetail),
    liked_by_me: getBooleanValue(r.liked_by_me) ?? false,
    likes_me: getBooleanValue(r.likes_me) ?? false,
    shared_events_count: getNumberValue(r.shared_events_count),
    participations_count: getNumberValue(r.participations_count ?? r.activity_participations),
    badge_count: getNumberValue(r.badge_count),
    match_id: getStringValue(r.match_id) ?? null,
    matched_at: getStringValue(r.matched_at) ?? null,
    liked_at: getStringValue(r.liked_at) ?? null,
    gender: normalizeGenderValue(r.gender),
    birthday: getNullableStringValue(r.birthday ?? r.birthdate) ?? null,
  };
};

const normalizeMembersPaginatedResponse = (data: unknown): PublicMembersResponse => {
  const root = toRecord(data) ?? {};
  const rawList: unknown[] = Array.isArray(data)
    ? (data as unknown[])
    : Array.isArray(root.results)
    ? (root.results as unknown[])
    : Array.isArray(root.members)
    ? (root.members as unknown[])
    : [];

  const members = rawList
    .map(mapRawToPublicMemberSummary)
    .filter(Boolean) as PublicMemberSummary[];

  return {
    members,
    count: getNumberValue(root.count) ?? members.length,
    next: (getStringValue(root.next) as string | null | undefined) ?? null,
    previous: (getStringValue(root.previous) as string | null | undefined) ?? null,
  };
};

/**
 * Discovery feed — recommended member cards (falls back to GET /api/users/ if 404).
 */
export const fetchSuggestedMembers = async (
  params: FetchSuggestedMembersParams = {},
): Promise<PublicMembersResponse> => {
  const headers = await getDualAuthHeaders();
  const searchParams = new URLSearchParams();
  if (params.page && params.page > 1) {
    searchParams.set('page', String(params.page));
  }
  if (params.gender) searchParams.set('gender', params.gender);
  if (params.min_age !== undefined) searchParams.set('min_age', String(params.min_age));
  if (params.max_age !== undefined) searchParams.set('max_age', String(params.max_age));
  if (params.interests) searchParams.set('interests', params.interests);
  if (params.activity_id !== undefined) {
    searchParams.set('activity_id', String(params.activity_id));
  }
  const qs = searchParams.toString();
  const url = `${API_BASE_URL}/api/users/suggested/${qs ? `?${qs}` : ''}`;
  const response = await fetch(url, { headers });
  if (response.status === 404) {
    const err = new Error('SUGGESTED_NOT_AVAILABLE');
    (err as Error & { status?: number }).status = 404;
    throw err;
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
  }
  const data = await response.json();
  return normalizeMembersPaginatedResponse(data);
};

export const likeUser = async (userId: string): Promise<LikeUserResponse> => {
  const headers = await getDualAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/users/${encodeURIComponent(userId)}/like/`, {
    method: 'POST',
    headers,
  });
  if (response.status === 400 || response.status === 404) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as { detail?: string }).detail || response.statusText);
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as LikeUserResponse;
};

export const unlikeUser = async (userId: string): Promise<void> => {
  const headers = await getDualAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/users/${encodeURIComponent(userId)}/like/`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok && response.status !== 204) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
};

/** Success payload or subscription gate (403) with teaser count — same pattern as profile views. */
export type ReceivedLikesResult =
  | PublicMembersResponse
  | { error: 'subscription_required'; count?: number; message?: string };

export const fetchReceivedLikes = async (page: number = 1): Promise<ReceivedLikesResult> => {
  const headers = await getDualAuthHeaders();
  const params = new URLSearchParams();
  if (page > 1) params.set('page', String(page));
  const url = `${API_BASE_URL}/api/users/likes/received/${params.toString() ? `?${params}` : ''}`;
  const response = await fetch(url, { headers });
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (response.status === 403) {
    const count =
      typeof data.count === 'number'
        ? data.count
        : typeof data.like_count === 'number'
          ? data.like_count
          : undefined;
    return {
      error: 'subscription_required',
      count,
      message: typeof data.message === 'string' ? data.message : undefined,
    };
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return normalizeMembersPaginatedResponse(data);
};

export const fetchMutualMatches = async (page: number = 1): Promise<PublicMembersResponse> => {
  const headers = await getDualAuthHeaders();
  const params = new URLSearchParams();
  if (page > 1) params.set('page', String(page));
  const url = `${API_BASE_URL}/api/users/likes/mutual/${params.toString() ? `?${params}` : ''}`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  return normalizeMembersPaginatedResponse(data);
};

/** Record profile view (Phase 3); safe to call if endpoint missing. */
export const recordProfileView = async (userId: string): Promise<void> => {
  try {
    const headers = await getDualAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/users/${encodeURIComponent(userId)}/view/`, {
      method: 'POST',
      headers,
    });
    if (!response.ok && response.status !== 404) {
      console.warn('recordProfileView:', response.status);
    }
  } catch (e) {
    console.warn('recordProfileView failed', e);
  }
};

// Phase 2 — badges
export interface UserBadgeItem {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  earned_at?: string;
  category?: string;
}

export interface UserBadgesResponse {
  badges: UserBadgeItem[];
  total_badges: number;
  next_badge?: {
    id: string;
    name: string;
    description?: string;
    progress: number;
    target: number;
    category?: string;
  } | null;
}

export const fetchUserBadges = async (userId: string): Promise<UserBadgesResponse> => {
  const headers = await getDualAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/users/${encodeURIComponent(userId)}/badges/`, {
    headers,
  });
  if (response.status === 404) {
    return { badges: [], total_badges: 0, next_badge: null };
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as UserBadgesResponse;
};

// Phase 3 — profile views & boost
export interface ProfileViewEntry {
  viewer: {
    user_id: string;
    name: string;
    profile_picture_url?: string;
    city?: string;
    country?: string;
  };
  viewed_at: string;
}

export interface ProfileViewsReceivedSuccess {
  count: number;
  next: string | null;
  previous?: string | null;
  results: ProfileViewEntry[];
}

export type ProfileViewsReceivedResult =
  | ProfileViewsReceivedSuccess
  | { error: 'subscription_required'; view_count?: number; message?: string };

export const fetchProfileViewsReceived = async (page: number = 1): Promise<ProfileViewsReceivedResult> => {
  const headers = await getDualAuthHeaders();
  const params = new URLSearchParams();
  if (page > 1) params.set('page', String(page));
  const q = params.toString();
  const url = `${API_BASE_URL}/api/users/views/received/${q ? `?${q}` : ''}`;
  const response = await fetch(url, { headers });
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (response.status === 403) {
    return {
      error: 'subscription_required',
      view_count: typeof data.view_count === 'number' ? data.view_count : undefined,
      message: typeof data.message === 'string' ? data.message : undefined,
    };
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const results = Array.isArray(data.results) ? (data.results as ProfileViewEntry[]) : [];
  return {
    count: typeof data.count === 'number' ? data.count : results.length,
    next: typeof data.next === 'string' ? data.next : null,
    previous: typeof data.previous === 'string' ? data.previous : null,
    results,
  };
};

export interface ProfileViewsCountResponse {
  view_count: number;
  new_views_since_last_check?: number;
}

export const fetchProfileViewsCount = async (): Promise<ProfileViewsCountResponse | null> => {
  try {
    const headers = await getDualAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/users/views/received/count/`, { headers });
    if (!response.ok) {
      return null;
    }
    return (await response.json()) as ProfileViewsCountResponse;
  } catch {
    return null;
  }
};

export interface ProfileBoostInfo {
  id?: number;
  started_at?: string;
  expires_at?: string;
  duration_minutes?: number;
  remaining_minutes?: number;
  is_active?: boolean;
}

export interface ProfileBoostStatusResponse {
  is_active: boolean;
  boosts_remaining_today?: number;
  current_boost?: ProfileBoostInfo;
  last_boost?: ProfileBoostInfo;
  next_boost_available_at?: string;
}

export const fetchProfileBoostStatus = async (): Promise<ProfileBoostStatusResponse | null> => {
  try {
    const headers = await getDualAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/users/boost/status/`, { headers });
    if (!response.ok) {
      return null;
    }
    return (await response.json()) as ProfileBoostStatusResponse;
  } catch {
    return null;
  }
};

export const activateProfileBoost = async (): Promise<{ message?: string; boost?: ProfileBoostInfo }> => {
  const headers = await getDualAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/users/boost/`, {
    method: 'POST',
    headers,
  });
  const data = (await response.json().catch(() => ({}))) as { message?: string; boost?: ProfileBoostInfo; error?: string };
  if (!response.ok) {
    const msg = data.message || data.error || `HTTP ${response.status}`;
    const err = new Error(msg) as Error & { status: number; body: unknown };
    err.status = response.status;
    err.body = data;
    throw err;
  }
  return data;
};

export interface PublicMemberDetail extends PublicMemberSummary {
  joinedAt?: string;
  recentActivities?: PublicMemberActivity[];
  upcomingActivities?: PublicMemberActivity[];
}

const hashStringToPositiveNumber = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  hash = Math.abs(hash);
  return hash === 0 ? value.length || 1 : hash;
};

const ensureNumericId = (value: unknown, fallbackSeed: string): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
    return hashStringToPositiveNumber(value);
  }
  return hashStringToPositiveNumber(fallbackSeed);
};

const toRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const getStringValue = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const getNullableStringValue = (value: unknown): string | null | undefined =>
  typeof value === 'string' ? value : value === null ? null : undefined;

const getNumberValue = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const getBooleanValue = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

const getArrayValue = (value: unknown): unknown[] | undefined =>
  Array.isArray(value) ? value : undefined;

const getInterestsArray = (value: unknown): PublicMemberInterest[] | undefined =>
  Array.isArray(value) ? (value as PublicMemberInterest[]) : undefined;

const normalizeMemberActivity = (activity: unknown): PublicMemberActivity | null => {
  const activityRecord = toRecord(activity);
  if (!activityRecord) {
    return null;
  }

  const nested = toRecord(activityRecord.activity);
  const source = nested ?? activityRecord;

  const rawId = source.id ?? activityRecord.id;

  const titleCandidate = source.title ?? activityRecord.title;
  const title = getStringValue(titleCandidate) ?? 'Untitled activity';

  const dateCandidate = source.date ?? activityRecord.date;
  const date = getStringValue(dateCandidate) ?? '';

  const locationNameCandidate = source.location_name ?? activityRecord.location_name;
  const id = ensureNumericId(rawId, `${title}-${date}-${getStringValue(locationNameCandidate) ?? ''}`);

  if (!title && !date) {
    return null;
  }

  const time = getStringValue(source.time ?? activityRecord.time);
  const city = getStringValue(source.city ?? activityRecord.city);
  const country = getStringValue(source.country ?? activityRecord.country);
  const location_name = getStringValue(locationNameCandidate);
  const cover_image =
    getNullableStringValue(source.cover_image ?? activityRecord.cover_image) ?? null;

  return {
    id,
    title,
    date,
    time,
    city,
    country,
    location_name,
    cover_image,
  };
};

const normalizeMemberActivities = (list: unknown): PublicMemberActivity[] => {
  if (!Array.isArray(list)) {
    return [];
  }
  return list.map(normalizeMemberActivity).filter(Boolean) as PublicMemberActivity[];
};

const normalizeGalleryItems = (items: unknown): GalleryMediaItem[] | undefined => {
  const entries = getArrayValue(items);
  if (!entries) {
    return undefined;
  }

  const mapped = entries
    .map((item, index) => {
      const record = toRecord(item);
      if (!record) {
        return null;
      }
      const imageUrl =
        getStringValue(record.image_url ?? record.url ?? record.imageUrl);
      if (!imageUrl) {
        return null;
      }
      const id = ensureNumericId(record.id, `${imageUrl}-${index}`);

      return {
        id,
        image_url: imageUrl,
        caption: getNullableStringValue(record.caption ?? record.description) ?? null,
        is_public: getBooleanValue(record.is_public) ?? true,
        display_order: getNumberValue(record.display_order) ?? 0,
        created_at: getStringValue(record.created_at) ?? '',
        updated_at: getStringValue(record.updated_at) ?? '',
      } as GalleryMediaItem;
    })
    .filter(Boolean) as GalleryMediaItem[];

  return mapped.length ? mapped : undefined;
};

const normalizeMemberDetail = (raw: unknown, userId: string): PublicMemberDetail => {
  const record = toRecord(raw);
  if (!record) {
    return {
      userId,
      name: 'Community Member',
      activities: [],
      createdActivities: [],
    };
  }

  const primaryUserIdValue =
    record.userId ??
    record.user_id ??
    record.uid ??
    record.id ??
    userId;

  const primaryUserId =
    getStringValue(primaryUserIdValue) ?? String(primaryUserIdValue ?? userId);

  const firstName = getStringValue(record.first_name ?? record.firstName);
  const lastName = getStringValue(record.last_name ?? record.lastName);
  const fallbackName = [firstName, lastName].filter(Boolean).join(' ');

  const gallery = normalizeGalleryItems(record.gallery);
  const galleryPreview =
    normalizeGalleryItems(record.galleryPreview) ?? normalizeGalleryItems(record.gallery_preview);

  const recentActivities =
    normalizeMemberActivities(record.recentActivities ?? record.recent_created_activities);
  const upcomingActivities = normalizeMemberActivities(record.upcomingActivities ?? record.upcoming_activities);
  const createdActivities = recentActivities.length
    ? recentActivities
    : normalizeMemberActivities(record.createdActivities ?? record.created_activities);
  const explicitActivities = normalizeMemberActivities(record.activities);
  const activities = explicitActivities.length
    ? explicitActivities
    : createdActivities.length
    ? createdActivities
    : recentActivities;

  const interestsDetail =
    getInterestsArray(record.interests_detail) ??
    getInterestsArray(record.interestsDetail) ??
    undefined;

  const interests =
    getInterestsArray(record.interests) ??
    interestsDetail ??
    undefined;

  const detail: PublicMemberDetail = {
    userId: primaryUserId,
    id: record.id ? String(record.id) : undefined,
    name:
      (getStringValue(record.name) ??
        getStringValue(record.full_name) ??
        fallbackName) ||
      getStringValue(record.username) ||
      'Community Member',
    username: getStringValue(record.username ?? record.handle),
    profilePictureUrl:
      getStringValue(
        record.profilePictureUrl ??
          record.profile_picture_url ??
          record.profilePictureURL ??
          record.avatar_url,
      ) ?? undefined,
    bio: getStringValue(record.bio ?? record.about),
    city: getStringValue(record.city ?? record.location_city) ?? null,
    country: getStringValue(record.country ?? record.location_country) ?? null,
    isOrganizer: Boolean(
      getBooleanValue(record.is_organizer) ??
        getBooleanValue(record.isOrganizer) ??
        ((getNumberValue(record.created_activities_count) ?? 0) > 0),
    ),
    interests,
    interests_detail: interestsDetail,
    gallery,
    galleryPreview: galleryPreview ?? gallery,
    galleryCount:
      getNumberValue(record.galleryCount ?? record.gallery_count) ?? (gallery?.length ?? undefined),
    galleryPrivateCount: getNumberValue(record.galleryPrivateCount ?? record.gallery_private_count),
    galleryLimit: getNumberValue(record.galleryLimit ?? record.gallery_limit),
    activities,
    createdActivities,
    recentActivities,
    upcomingActivities,
    joinedAt:
      getStringValue(record.joinedAt ?? record.joined_at ?? record.created_at) ?? undefined,
    gender:
      normalizeGenderValue(
        record.gender ??
          record.gender_identity ??
          record.genderIdentity ??
          record.pronouns ??
          record.gender_preference,
      ) ?? null,
    birthday:
      getNullableStringValue(
        record.birthday ??
          record.birthdate ??
          record.birth_date ??
          record.date_of_birth ??
          record.dob,
      ) ?? null,
    liked_by_me:
      getBooleanValue(record.liked_by_me) ?? getBooleanValue(record.likedByMe) ?? undefined,
    likes_me: getBooleanValue(record.likes_me) ?? getBooleanValue(record.likesMe) ?? undefined,
    shared_events_count: getNumberValue(record.shared_events_count ?? record.sharedEventsCount),
    participations_count: getNumberValue(
      record.participations_count ?? record.activity_participations ?? record.participation_count,
    ),
    badge_count: getNumberValue(record.badge_count ?? record.badgeCount),
  };

  if ('email' in detail) {
    delete (detail as unknown as { email?: string }).email;
  }

  return detail;
};

/**
 * Fetch detailed public profile for a member.
 * Tries authenticated detail endpoint first, then falls back to public summaries.
 */
export const fetchPublicMemberProfile = async (userId: string): Promise<PublicMemberDetail> => {
  console.log('🔍 fetchPublicMemberProfile called for:', userId);

  const headers = await getDualAuthHeaders();

  // Attempt authenticated detail endpoint first (returns organizer/member stats)
  const authenticatedUrl = `${API_BASE_URL}/api/users/${userId}/`;
  try {
    console.log('🌐 Trying authenticated member endpoint:', authenticatedUrl);
    const response = await fetch(authenticatedUrl, { headers });
    if (response.ok) {
      const data = await response.json();
      console.log('✅ Member profile loaded from authenticated endpoint');
      return normalizeMemberDetail(data, userId);
    }

    console.warn('⚠️ Authenticated endpoint returned', response.status, response.statusText);
  } catch (error) {
    console.warn('⚠️ Authenticated endpoint failed:', error);
  }

  const candidatePaths = [
    `${API_BASE_URL}/api/users/${userId}/public_profile/`,
    `${API_BASE_URL}/api/users/${userId}/public/`,
    `${API_BASE_URL}/api/users/${userId}/summary/`,
  ];

  for (const url of candidatePaths) {
    try {
      console.log('🌐 Trying fallback member endpoint:', url);
      const response = await fetch(url, { headers });

      if (response.status === 404) {
        console.warn('ℹ️ Member profile endpoint not found:', url);
        continue;
      }

      if (!response.ok) {
        console.warn('⚠️ Member profile endpoint returned error:', response.status, response.statusText);
        continue;
      }

      const data = await response.json();
      console.log('✅ Member profile loaded from endpoint:', url);

      return normalizeMemberDetail(data, userId);
    } catch (error) {
      console.warn('⚠️ Error fetching member profile from endpoint:', url, error);
    }
  }

  throw new Error('Unable to load member profile');
};

// =============================================================================
// END PUBLIC API FUNCTIONS
// =============================================================================

// Fetch user's registered activities
export const fetchUserRegisteredActivities = async (): Promise<PaginatedActivities> => {
  console.log('🔍 fetchUserRegisteredActivities called');

  if (USE_MOCK_DATA) {
    console.log('📝 Using mock data for registered activities');
    return fetchMockActivities(1);
  }

  try {
    const url = `${API_BASE_URL}/api/user/registered-activities/`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const response = await fetch(url, { headers });
    
    console.log('📡 Registered Activities Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: PaginatedActivities = await response.json();
    console.log('✅ Registered Activities Success! Got', data.count, 'activities');
    return data;
  } catch (error) {
    console.error('❌ Registered Activities Error:', error);
    return { count: 0, results: [] };
  }
};

// Fetch user's participations with status
// UPDATED: Now uses authenticatedFetch for automatic 401 handling
export const fetchUserParticipations = async (): Promise<PaginatedActivities> => {
  console.log('🔍 fetchUserParticipations called');

  if (USE_MOCK_DATA) {
    console.log('📝 Using mock data for participations');
    return fetchMockActivities(1);
  }

  try {
    const url = `${API_BASE_URL}/api/activities/my_participations/`;
    console.log('🌐 Attempting API call to:', url);
    
    // Using authenticatedFetch - automatically handles 401 errors with token refresh + retry
    const response = await authenticatedFetch(url);
    
    console.log('📡 Participations Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: PaginatedActivities = await response.json();
    console.log('✅ Participations Success! Got', data.count, 'participations');
    return data;
  } catch (error) {
    console.error('❌ Participations Error:', error);
    return { count: 0, results: [] };
  }
};

// Fetch activities for a specific date range
export const fetchActivitiesByDate = async (dateFrom: string, dateTo?: string, city?: string, country?: string, category?: number): Promise<PaginatedActivities> => {
  console.log('🔍 fetchActivitiesByDate called with:', { dateFrom, dateTo, city, country, category });

  if (USE_MOCK_DATA) {
    console.log('📝 Using mock data for date-filtered activities');
    return fetchMockActivities(1);
  }

  try {
    const params = new URLSearchParams({
      dateFrom,
      page: '1',
    });
    if (dateTo) {
      params.append('dateTo', dateTo);
    }
    if (city) {
      params.append('city', city);
      console.log('🏙️ Filtering by city:', city);
    }
    if (country) {
      params.append('country', country);
      console.log('🌍 Filtering by country:', country);
    }
    if (category) {
      params.append('category', category.toString());
      console.log('🏷️ Filtering by category:', category);
    }

    const url = `${API_BASE_URL}/api/activities/?${params.toString()}`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const response = await fetch(url, { headers });
    
    console.log('📡 Date Activities Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: PaginatedActivities = await response.json();
    console.log('✅ Date Activities Success! Got', data.count, 'activities');
    return data;
  } catch (error) {
    console.error('❌ Date Activities Error:', error);
    return { count: 0, results: [] };
  }
};

export const fetchActivities = async (page: number = 1, city?: string, country?: string, category?: number): Promise<PaginatedActivities> => {
  console.log('🔍 fetchActivities called with:', {
    page,
    city,
    country,
    category,
    API_BASE_URL,
    USE_MOCK_DATA,
    hasApiKey: !!import.meta.env.VITE_API_KEY,
    environment: import.meta.env.MODE
  });

  // Use mock data if enabled (for testing while CORS is being fixed)
  if (USE_MOCK_DATA) {
    console.log('📝 Using mock data (VITE_USE_MOCK_DATA=true)');
    return fetchMockActivities(page);
  }

  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    if (city) {
      params.append('city', city);
      console.log('🏙️ Filtering by city:', city);
    }
    if (country) {
      params.append('country', country);
      console.log('🌍 Filtering by country:', country);
    }
    if (category) {
      params.append('category', category.toString());
      console.log('🏷️ Filtering by category:', category);
    }
    
    const url = `${API_BASE_URL}/api/activities/?${params.toString()}`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const headerObj = headers as Record<string, string>;
    console.log('📋 Request headers:', {
      'Content-Type': headerObj['Content-Type'],
      'X-API-KEY': headerObj['X-API-KEY'] ? 'SET' : 'MISSING',
      'Authorization': headerObj['Authorization'] ? 'Bearer TOKEN_SET' : 'MISSING',
    });
    
    const response = await fetch(url, { headers });
    
    console.log('📡 API Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: PaginatedActivities = await response.json();
    console.log('✅ API Success! Got', data.count, 'activities');
    return data;
  } catch (error) {
    console.error('❌ API Error - falling back to mock data:', error);
    console.error('Error details:', {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined
    });
    // Fallback to mock data if real API fails (CORS or other issues)
    return fetchMockActivities(page);
  }
}; 

// **NEW USER-RELATED API FUNCTIONS**

// Sync/onboard user (get or create profile)
export const syncUser = async (): Promise<UserProfile> => {
  console.log('🔍 syncUser called');

  try {
    const url = `${API_BASE_URL}/api/user/sync/`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getDualAuthHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers,
    });
    
    console.log('📡 User Sync Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: UserProfile = await response.json();
    console.log('✅ User Sync Success!');
    return data;
  } catch (error) {
    console.error('❌ User Sync Error:', error);
    throw error;
  }
};

// Get current user profile
export const fetchCurrentUserProfile = async (): Promise<UserProfile> => {
  console.log('🔍 fetchCurrentUserProfile called');

  try {
    const url = `${API_BASE_URL}/api/user/me/`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getDualAuthHeaders();
    const response = await fetch(url, { headers });
    
    console.log('📡 User Profile Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: UserProfile = await response.json();
    console.log('✅ User Profile Success!');
    console.log('💳 Subscription Info:', {
      type: data.subscription.type,
      status: data.subscription.status,
      can_create_activity: data.subscription.can_create_activity,
      activities_remaining: data.subscription.activities_remaining,
      credit_balance: data.subscription.credit_balance,
      plan: data.subscription.plan?.display_name || 'No plan',
      trial_active: data.subscription.trial?.is_active || false,
      trial_days_remaining: data.subscription.trial?.days_remaining || 0
    });
    return data;
  } catch (error) {
    console.error('❌ User Profile Error:', error);
    throw error;
  }
};

// Update user profile
export const updateUserProfile = async (profileData: UserProfileUpdate): Promise<UserProfile> => {
  console.log('🔍 updateUserProfile called with:', profileData);

  try {
    const url = `${API_BASE_URL}/api/user/me/update/`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getDualAuthHeaders();
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(profileData),
    });
    
    console.log('📡 Update Profile Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status} ${response.statusText}`;
      try {
        const errorText = await response.text();
        if (errorText) {
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = JSON.stringify(errorJson);
          } catch {
            errorMessage = errorText;
          }
        }
      } catch (readError) {
        console.warn('⚠️ Failed to read error response body:', readError);
      }
      throw new Error(errorMessage);
    }
    
    const data: UserProfile = await response.json();
    console.log('✅ Update Profile Success!');
    return data;
  } catch (error) {
    console.error('❌ Update Profile Error:', error);
    throw error;
  }
};

export const fetchMyGallery = async (): Promise<PaginatedGalleryResponse> => {
  try {
    console.log('🖼️ Fetching current user gallery');
    const url = `${API_BASE_URL}/api/user/me/media/`;
    const headers = await getDualAuthHeaders();
    const response = await fetch(url, { headers });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch gallery:', response.status, errorText);
      throw new Error(`Failed to load gallery (${response.status})`);
    }

    const data: PaginatedGalleryResponse = await response.json();
    console.log('✅ Gallery fetched with', data.results.length, 'items');
    return data;
  } catch (error) {
    console.error('❌ Gallery fetch error:', error);
    throw error;
  }
};

export const createGalleryMedia = async (payload: CreateGalleryMediaRequest): Promise<GalleryMediaItem> => {
  try {
    console.log('🖼️ Adding gallery media:', {
      hasCaption: !!payload.caption,
      is_public: payload.is_public,
      hasDisplayOrder: payload.display_order !== undefined,
    });
    const url = `${API_BASE_URL}/api/user/me/media/`;
    const headers = await getDualAuthHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to add gallery media:', response.status, errorText);
      throw new Error(errorText || `Failed to add gallery media (${response.status})`);
    }

    const data: GalleryMediaItem = await response.json();
    console.log('✅ Gallery media created with ID', data.id);
    return data;
  } catch (error) {
    console.error('❌ Gallery create error:', error);
    throw error;
  }
};

export const updateGalleryMedia = async (
  mediaId: number,
  payload: UpdateGalleryMediaRequest
): Promise<GalleryMediaItem> => {
  try {
    console.log('🛠️ Updating gallery media:', mediaId, payload);
    const url = `${API_BASE_URL}/api/user/me/media/${mediaId}/`;
    const headers = await getDualAuthHeaders();
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to update gallery media:', response.status, errorText);
      throw new Error(errorText || `Failed to update gallery media (${response.status})`);
    }

    const data: GalleryMediaItem = await response.json();
    console.log('✅ Gallery media updated:', data.id);
    return data;
  } catch (error) {
    console.error('❌ Gallery update error:', error);
    throw error;
  }
};

export const deleteGalleryMedia = async (mediaId: number): Promise<void> => {
  try {
    console.log('🗑️ Deleting gallery media:', mediaId);
    const url = `${API_BASE_URL}/api/user/me/media/${mediaId}/`;
    const headers = await getDualAuthHeaders();
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok && response.status !== 204) {
      const errorText = await response.text();
      console.error('❌ Failed to delete gallery media:', response.status, errorText);
      throw new Error(errorText || `Failed to delete gallery media (${response.status})`);
    }

    console.log('✅ Gallery media deleted:', mediaId);
  } catch (error) {
    console.error('❌ Gallery delete error:', error);
    throw error;
  }
};

export const fetchUserGallery = async (firebaseUid: string): Promise<PaginatedGalleryResponse> => {
  try {
    console.log('🖼️ Fetching gallery for user:', firebaseUid);
    const url = `${API_BASE_URL}/api/users/${firebaseUid}/media/`;
    const headers = await getAuthHeaders();
    const response = await fetch(url, { headers });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch user gallery:', response.status, errorText);
      throw new Error(errorText || `Failed to fetch gallery (${response.status})`);
    }

    const data: PaginatedGalleryResponse = await response.json();
    console.log('✅ User gallery fetched with', data.results.length, 'items');
    return data;
  } catch (error) {
    console.error('❌ User gallery fetch error:', error);
    throw error;
  }
};

// Delete user account
export const deleteAccount = async (): Promise<AccountDeletionResponse> => {
  console.log('🔍 deleteAccount called');

  try {
    const url = `${API_BASE_URL}/api/user/me/delete/`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getDualAuthHeaders();
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });
    
    console.log('📡 Delete Account Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Delete Account Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: AccountDeletionResponse = await response.json();
    console.log('✅ Delete Account Success!', data);
    return data;
  } catch (error) {
    console.error('❌ Delete Account Error:', error);
    throw error;
  }
};

// Export user data (GDPR Article 20 - Right to Data Portability)
export interface UserDataExport {
  user: {
    firebase_uid: string;
    email: string;
    username: string;
    first_name: string | null;
    last_name: string | null;
    bio: string | null;
    profile_picture_url: string | null;
    city: string | null;
    country: string | null;
    gender: string | null;
    birthday: string | null;
    is_verified: boolean;
    is_active: boolean;
    date_joined: string;
    last_login: string | null;
  };
  activities_created: Array<{
    id: number;
    title: string;
    description: string;
    created_at: string;
  }>;
  participations: Array<{
    activity_id: number;
    activity_title: string;
    joined_at: string;
    status: string;
  }>;
  reviews: Array<{
    id: number;
    activity_id: number;
    rating: number;
    comment: string | null;
    created_at: string;
  }>;
  groups: Array<{
    id: number;
    name: string;
    role: string;
    joined_at: string;
  }>;
  exported_at: string;
}

export const exportUserData = async (): Promise<UserDataExport> => {
  console.log('🔍 exportUserData called (GDPR Article 20)');

  try {
    const url = `${API_BASE_URL}/api/user/me/export/`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getDualAuthHeaders();
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    console.log('📡 Export User Data Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Export User Data Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: UserDataExport = await response.json();
    console.log('✅ Export User Data Success!');
    return data;
  } catch (error) {
    console.error('❌ Export User Data Error:', error);
    throw error;
  }
};

// Get organizer stats for a user
export const fetchOrganizerStats = async (firebaseUid: string): Promise<OrganizerStats> => {
  console.log('🔍 fetchOrganizerStats called for UID:', firebaseUid);

  try {
    const url = `${API_BASE_URL}/api/users/${firebaseUid}/`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const response = await fetch(url, { headers });
    
    console.log('📡 Organizer Stats Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: OrganizerStats = await response.json();
    console.log('✅ Organizer Stats Success!');
    return data;
  } catch (error) {
    console.error('❌ Organizer Stats Error:', error);
    throw error;
  }
};

// Get activities created by current user
export const fetchUserCreatedActivities = async (page: number = 1): Promise<PaginatedActivities> => {
  console.log('🔍 fetchUserCreatedActivities called with page:', page);

  if (USE_MOCK_DATA) {
    console.log('📝 Using mock data for created activities');
    return fetchMockActivities(page);
  }

  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    
    const url = `${API_BASE_URL}/api/activities/my_created/?${params.toString()}`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const response = await fetch(url, { headers });
    
    console.log('📡 Created Activities Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      page: page,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: PaginatedActivities = await response.json();
    console.log('✅ Created Activities Success! Got', data.results.length, 'activities on page', page, 'of total', data.count);
    return data;
  } catch (error) {
    console.error('❌ Created Activities Error:', error);
    return { count: 0, results: [] };
  }
};

// Get all categories
export const fetchCategories = async (): Promise<Category[]> => {
  console.log('🔍 fetchCategories called');

  try {
    const url = `${API_BASE_URL}/api/categories/`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const response = await fetch(url, { headers });
    
    console.log('📡 Categories Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('✅ Categories Success! Raw data:', data);
    
    // Handle both array and object response formats
    if (Array.isArray(data)) {
      console.log('📋 Categories as array, length:', data.length);
      return data;
    } else if (data && Array.isArray(data.results)) {
      console.log('📋 Categories as paginated response, length:', data.results.length);
      return data.results;
    } else {
      console.warn('⚠️ Unexpected categories response format:', data);
      return [];
    }
  } catch (error) {
    console.error('❌ Categories Error:', error);
    return [];
  }
};

// Search activities with enhanced parameters
export const searchActivities = async (searchParams: SearchParams | string): Promise<PaginatedActivities> => {
  // Handle both string (backward compatibility) and SearchParams object
  const params = typeof searchParams === 'string' 
    ? { q: searchParams } 
    : searchParams;

  console.log('🔍 searchActivities called with params:', params);

  if (USE_MOCK_DATA) {
    console.log('📝 Using mock data for search');
    return fetchMockActivities(1);
  }

  try {
    const urlParams = new URLSearchParams();
    
    // Add required search term
    if (params.q) {
      urlParams.append('q', params.q);
    } else {
      throw new Error('Search term (q) is required');
    }
    
    // Add optional parameters
    if (params.category) {
      urlParams.append('category', params.category.toString());
    }
    if (params.min_lat !== undefined) {
      urlParams.append('min_lat', params.min_lat.toString());
    }
    if (params.max_lat !== undefined) {
      urlParams.append('max_lat', params.max_lat.toString());
    }
    if (params.min_lon !== undefined) {
      urlParams.append('min_lon', params.min_lon.toString());
    }
    if (params.max_lon !== undefined) {
      urlParams.append('max_lon', params.max_lon.toString());
    }

    const url = `${API_BASE_URL}/api/activities/search/?${urlParams.toString()}`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const response = await fetch(url, { headers });
    
    console.log('📡 Search Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: SearchResponse = await response.json();
    console.log('✅ Search Success! Got', data.results_count, 'activities for term:', data.search_term);
    
    // Convert SearchResponse to PaginatedActivities format for compatibility
    const paginatedResponse: PaginatedActivities = {
      count: data.results_count,
      results: data.activities,
      next: undefined, // Search doesn't provide pagination info
      previous: undefined,
    };
    
    return paginatedResponse;
  } catch (error) {
    console.error('❌ Search Error:', error);
    return { count: 0, results: [] };
  }
}; 

// Convenience function for location-based search
export const searchActivitiesByLocation = async (
  query: string,
  bounds?: {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
  },
  category?: number
): Promise<PaginatedActivities> => {
  const searchParams: SearchParams = {
    q: query,
    ...(category && { category }),
    ...(bounds && {
      min_lat: bounds.minLat,
      max_lat: bounds.maxLat,
      min_lon: bounds.minLon,
      max_lon: bounds.maxLon,
    }),
  };
  
  return searchActivities(searchParams);
};

// Convenience function for category-specific search
export const searchActivitiesByCategory = async (
  query: string,
  categoryId: number
): Promise<PaginatedActivities> => {
  const searchParams: SearchParams = {
    q: query,
    category: categoryId,
  };
  
  return searchActivities(searchParams);
};

// Get list of organizers with optional city/country filtering
export const fetchOrganizers = async (
  search?: string, 
  city?: string, 
  country?: string
): Promise<OrganizersResponse> => {
  console.log('🔍 fetchOrganizers called', { search, city, country });

  try {
    const params = new URLSearchParams();
    if (search) {
      params.append('search', search);
    }
    if (city) {
      params.append('city', city);
    }
    if (country) {
      params.append('country', country);
    }
    
    const url = `${API_BASE_URL}/api/users/organizers/${params.toString() ? `?${params.toString()}` : ''}`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const response = await fetch(url, { headers });
    
    console.log('📡 Organizers Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: OrganizersResponse = await response.json();
    console.log('✅ Organizers Success! Got', data.total_organizers, 'organizers', city ? `from ${city}` : '');
    return data;
  } catch (error) {
    console.error('❌ Organizers Error:', error);
    // Return empty response on error
    return { organizers: [], total_organizers: 0 };
  }
}; 

// Fetch activities within map bounding box
export const fetchActivitiesByBounds = async (
  bounds: MapBounds,
  category?: number
): Promise<PaginatedActivities> => {
  console.log('🗺️ fetchActivitiesByBounds called with bounds:', bounds, 'category:', category);

  if (USE_MOCK_DATA) {
    console.log('📝 Using mock data for map activities');
    return fetchMockActivities(1);
  }

  try {
    const params = new URLSearchParams();
    
    // Add required bounding box parameters
    params.append('min_lat', bounds.minLat.toString());
    params.append('max_lat', bounds.maxLat.toString());
    params.append('min_lon', bounds.minLon.toString());
    params.append('max_lon', bounds.maxLon.toString());
    
    // Add optional category filter
    if (category) {
      params.append('category', category.toString());
    }

    const url = `${API_BASE_URL}/api/activities/?${params.toString()}`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const response = await fetch(url, { headers });
    
    console.log('📡 Map Activities Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    
    const data: PaginatedActivities = await response.json();
    console.log('✅ Map Activities Success! Got', data.count, 'activities in bounds');
    return data;
  } catch (error) {
    console.error('❌ Map Activities Error:', error);
    return { count: 0, results: [] };
  }
};

// Search activities with map bounds (combines search with geographic filtering)
export const searchActivitiesWithBounds = async (
  query: string,
  bounds: MapBounds,
  category?: number
): Promise<PaginatedActivities> => {
  console.log('🗺️🔍 searchActivitiesWithBounds called:', { query, bounds, category });

  if (USE_MOCK_DATA) {
    console.log('📝 Using mock data for map search');
    return fetchMockActivities(1);
  }

  try {
    const searchParams: SearchParams = {
      q: query,
      min_lat: bounds.minLat,
      max_lat: bounds.maxLat,
      min_lon: bounds.minLon,
      max_lon: bounds.maxLon,
      ...(category && { category }),
    };
    
    return searchActivities(searchParams);
  } catch (error) {
    console.error('❌ Map Search Error:', error);
    return { count: 0, results: [] };
  }
};

// Get activities near a specific location (radius-based)
export const fetchActivitiesNearLocation = async (
  latitude: number,
  longitude: number,
  radiusKm: number = 10,
  category?: number
): Promise<PaginatedActivities> => {
  // Convert radius to approximate lat/lng bounds
  const latOffset = radiusKm / 111; // Approximate km per degree latitude
  const lonOffset = radiusKm / (111 * Math.cos(latitude * Math.PI / 180)); // Adjust for longitude

  const bounds: MapBounds = {
    minLat: latitude - latOffset,
    maxLat: latitude + latOffset,
    minLon: longitude - lonOffset,
    maxLon: longitude + lonOffset,
  };

  return fetchActivitiesByBounds(bounds, category);
};

// Activity Management APIs

export const updateActivity = async (
  activityId: number,
  activityData: {
    title: string;
    description: string;
    category_id: number;
    tags_id: number[];
    date: string;
    time: string;
    end_time?: string | null;
    end_date?: string | null;
    location_name: string;
    address: string;
    city: string;
    country: string;
    latitude: number;
    longitude: number;
    max_participants: number | null;
    event_type: string;
    skill_level: string;
    image_url: string | null;
    is_publicly_visible?: boolean;
    is_registration_allowed?: boolean;
  }
): Promise<Activity> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/`;
  
  console.log('🌐 Attempting API call to:', url);
  console.log('📤 Request data:', JSON.stringify(activityData, null, 2));
  
  const headers = await getAuthHeaders();
  console.log('🔐 Headers:', { 
    'Content-Type': (headers as Record<string, string>)['Content-Type'],
    'X-API-KEY': (headers as Record<string, string>)['X-API-KEY'] ? '[REDACTED]' : 'Missing',
    'Authorization': (headers as Record<string, string>)['Authorization'] ? 'Bearer [REDACTED]' : 'Missing'
  });
  
  try {
    // Remove empty end_time to satisfy backend optional field expectations
    const payload: Record<string, unknown> = { ...activityData };
    if (payload.end_time === '' || payload.end_time === undefined || payload.end_time === null) {
      delete payload.end_time;
    }
    // Handle end_date: 
    // - Remove if empty string or undefined (new single-day activity)
    // - Keep null value to explicitly clear end_date when converting multi-day to single-day
    if (payload.end_date === '' || payload.end_date === undefined) {
      delete payload.end_date;
    }
    // Note: We explicitly keep null values for end_date to signal backend to clear the field
    // Keep max_participants as null when user intends unlimited; backend should handle null appropriately

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload),
    });

    console.log('📡 Update Activity Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries())
    });

    if (!response.ok) {
      // Try to get error details from response
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Server error details:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to update activity: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Update Activity Success!');
    
    return data;
  } catch (error) {
    console.error('❌ Update Activity Error:', error);
    throw error;
  }
};

export const cancelActivity = async (
  activityId: number,
  reason: string
): Promise<{ message: string; notifications_sent: boolean; notified_users_count: number; notification_details: { success_count: number; failure_count: number } }> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/cancel_activity/`;
  
  console.log('🌐 Attempting API call to:', url);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ reason }),
    });

    console.log('📡 Cancel Activity Response:', response);

    if (!response.ok) {
      throw new Error(`Failed to cancel activity: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Cancel Activity Success!');
    
    return data;
  } catch (error) {
    console.error('❌ Cancel Activity Error:', error);
    throw error;
  }
};

export const deleteActivity = async (activityId: number): Promise<{ message: string }> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/`;
  
  console.log('🌐 Attempting API call to:', url);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    console.log('📡 Delete Activity Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Activity not found or you do not have permission to delete it');
      }
      throw new Error(`Failed to delete activity: ${response.status} ${response.statusText}`);
    }

    // HTTP 204 No Content - successful deletion with no response body
    if (response.status === 204) {
      console.log('✅ Delete Activity Success! (No Content)');
      return { message: 'Activity successfully deleted' };
    }

    // If there's content, try to parse it
    const contentLength = response.headers.get('content-length');
    if (contentLength && contentLength !== '0') {
      const data = await response.json();
      console.log('✅ Delete Activity Success with response!');
      return data;
    } else {
      console.log('✅ Delete Activity Success! (Empty Response)');
      return { message: 'Activity successfully deleted' };
    }
  } catch (error) {
    console.error('❌ Delete Activity Error:', error);
    throw error;
  }
};

export const sendActivityReminders = async (
  hoursAhead: number
): Promise<{ message: string; activities_processed: number; notifications_sent: number; details: { success_count: number; failure_count: number } }> => {
  const url = `${API_BASE_URL}/api/activities/send_activity_reminders/`;
  
  console.log('🌐 Attempting API call to:', url);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ hours_ahead: hoursAhead }),
    });

    console.log('📡 Send Reminders Response:', response);

    if (!response.ok) {
      throw new Error(`Failed to send reminders: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Send Reminders Success!');
    
    return data;
  } catch (error) {
    console.error('❌ Send Reminders Error:', error);
    throw error;
  }
};

export const sendMessageToAttendees = async (
  activityId: number,
  message: string
): Promise<{ message: string; notifications_sent: boolean; notified_users_count: number; notification_details: { success_count: number; failure_count: number } }> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/send_message_to_attendees/`;
  
  console.log('🌐 Attempting API call to:', url);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message }),
    });

    console.log('📡 Send Message Response:', response);

    if (!response.ok) {
      throw new Error(`Failed to send message: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Send Message Success!');
    
    return data;
  } catch (error) {
    console.error('❌ Send Message Error:', error);
    throw error;
  }
};

// Create new activity
export const createActivity = async (
  activityData: {
    title: string;
    description: string;
    category_id: number;
    tags_id: number[];
    date: string;
    time: string;
    end_time?: string | null;
    end_date?: string | null;
    location_name: string;
    address: string;
    city: string;
    country: string;
    latitude: number;
    longitude: number;
    max_participants: number | null;
    event_type: string;
    skill_level: string;
    image_url: string | null;
    group_id?: number | null;
    is_publicly_visible?: boolean;
    is_registration_allowed?: boolean;
  }
): Promise<Activity> => {
  const url = `${API_BASE_URL}/api/activities/`;
  
  console.log('🌐 Attempting API call to:', url);
  console.log('📤 Create activity data:', JSON.stringify(activityData, null, 2));
  
  const headers = await getAuthHeaders();
  
  try {
    // Remove empty end_time to satisfy backend optional field expectations
    const payload: Record<string, unknown> = { ...activityData };
    if (payload.end_time === '' || payload.end_time === undefined || payload.end_time === null) {
      delete payload.end_time;
    }
    // Remove empty end_date to satisfy backend optional field expectations
    if (payload.end_date === '' || payload.end_date === undefined || payload.end_date === null) {
      delete payload.end_date;
    }
    // For unlimited participants on PUBLIC_GROUP, omit the field if null
    if (
      payload.max_participants === null &&
      (activityData as { event_type: string }).event_type === 'PUBLIC_GROUP'
    ) {
      delete payload.max_participants;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    console.log('📡 Create Activity Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      try {
        const errorData = await response.json();
        console.error('❌ Create activity error details:', errorData);
        
        // Check if this is a subscription-related error with new structure
        if (errorData.error && errorData.subscription) {
          console.log('💳 Subscription error detected:', {
            error: errorData.error,
            message: errorData.message,
            subscription_type: errorData.subscription.type,
            subscription_status: errorData.subscription.status,
            creditBalance: errorData.subscription.credit_balance,
            activitiesRemaining: errorData.subscription.activities_remaining,
            suggested_actions: errorData.suggested_actions
          });
          
          // Throw custom subscription error with new structure
          throw new ActivityCreationError(errorData.message || errorData.error, errorData);
        }
        
        // Handle other API errors
        const errorMessage = errorData.message || errorData.detail || errorData.error || JSON.stringify(errorData);
        throw new Error(`Failed to create activity: ${errorMessage}`);
        
      } catch (parseError) {
        // If we can't parse the error response, check if it's our custom error
        if (parseError instanceof ActivityCreationError) {
          throw parseError;
        }
        
        console.error('❌ Could not parse error response:', parseError);
        throw new Error(`Failed to create activity: HTTP ${response.status}: ${response.statusText}`);
      }
    }

    const data = await response.json();
    console.log('✅ Create Activity Success!');
    
    return data;
  } catch (error) {
    console.error('❌ Create Activity Error:', error);
    throw error;
  }
};

// Group Management APIs

export interface Group {
  id: number;
  name: string;
  description: string;
  creator: string;
  creator_name: string;
  member_count: number;
  members: Array<{
    id: number;
    user: string;
    user_name: string;
    user_email: string;
    user_profile_picture: string;
    role: string;
    joined_at: string;
  }>;
  created_at: string;
  updated_at: string;
}

export interface UserSearchResult {
  id: string;
  name: string;
  email: string;
  username: string;
  profilePictureUrl: string;
  first_name: string;
  last_name: string;
}

export interface UserSearchResponse {
  search_term: string;
  users: UserSearchResult[];
  total_found: number;
}

// Get user's created groups
export const fetchUserGroups = async (): Promise<Group[]> => {
  const url = `${API_BASE_URL}/api/groups/my_created_groups/`;
  
  console.log('🌐 Attempting API call to:', url);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, { headers });

    console.log('📡 User Groups Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch groups: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ User Groups Success!');
    
    return data;
  } catch (error) {
    console.error('❌ User Groups Error:', error);
    return [];
  }
};

// Search users for adding to groups
export const searchUsers = async (query: string, limit: number = 20): Promise<UserSearchResponse> => {
  const params = new URLSearchParams({
    q: query,
    limit: limit.toString(),
  });
  
  const url = `${API_BASE_URL}/api/groups/search_users/?${params.toString()}`;
  
  console.log('🌐 Attempting API call to:', url);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, { headers });

    console.log('📡 Search Users Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      throw new Error(`Failed to search users: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Search Users Success!');
    
    return data;
  } catch (error) {
    console.error('❌ Search Users Error:', error);
    return { search_term: query, users: [], total_found: 0 };
  }
};

// Create new group
export const createGroup = async (
  groupData: {
    name: string;
    description?: string;
    member_ids?: string[];
  }
): Promise<Group> => {
  const url = `${API_BASE_URL}/api/groups/`;
  
  console.log('🌐 Attempting API call to:', url);
  console.log('📤 Create group data:', JSON.stringify(groupData, null, 2));
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(groupData),
    });

    console.log('📡 Create Group Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Create group error details:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to create group: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Create Group Success!');
    
    return data;
  } catch (error) {
    console.error('❌ Create Group Error:', error);
    throw error;
  }
};

// Update group
export const updateGroup = async (
  groupId: number,
  groupData: {
    name: string;
    description?: string;
  }
): Promise<Group> => {
  const url = `${API_BASE_URL}/api/groups/${groupId}/`;
  
  console.log('🌐 Attempting API call to:', url);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(groupData),
    });

    console.log('📡 Update Group Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      throw new Error(`Failed to update group: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Update Group Success!');
    
    return data;
  } catch (error) {
    console.error('❌ Update Group Error:', error);
    throw error;
  }
};

// Delete group
export const deleteGroup = async (groupId: number): Promise<{ message: string }> => {
  const url = `${API_BASE_URL}/api/groups/${groupId}/`;
  
  console.log('🌐 Attempting API call to:', url);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    console.log('📡 Delete Group Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      throw new Error(`Failed to delete group: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Delete Group Success!');
    
    return data;
  } catch (error) {
    console.error('❌ Delete Group Error:', error);
    throw error;
  }
};

// Add members to existing group
export const addMembersToGroup = async (
  groupId: number,
  memberIds: string[],
  role: 'admin' | 'member' = 'member'
): Promise<{ message: string; added_users: Array<{ user_id: string; name: string; role: string }>; errors: string[]; group: Group }> => {
  const url = `${API_BASE_URL}/api/groups/${groupId}/add_members/`;
  
  console.log('🌐 Attempting API call to:', url);
  console.log('📤 Add members data:', { user_ids: memberIds, role });
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ user_ids: memberIds, role }),
    });

    console.log('📡 Add Members Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Add members error details:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to add members: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Add Members Success!');
    
    return data;
  } catch (error) {
    console.error('❌ Add Members Error:', error);
    throw error;
  }
};

// Remove members from existing group
export const removeMembersFromGroup = async (
  groupId: number,
  memberIds: string[]
): Promise<{ message: string; removed_users: Array<{ user_id: string; name: string }>; errors: string[]; group: Group }> => {
  const url = `${API_BASE_URL}/api/groups/${groupId}/remove_members/`;
  
  console.log('🌐 Attempting API call to:', url);
  console.log('📤 Remove members data:', { user_ids: memberIds });
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ user_ids: memberIds }),
    });

    console.log('📡 Remove Members Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Remove members error details:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to remove members: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Remove Members Success!');
    
    return data;
  } catch (error) {
    console.error('❌ Remove Members Error:', error);
    throw error;
  }
}; 

// ============================
// PARTICIPANT MANAGEMENT APIs
// ============================

// Interface for attendee data
export interface ActivityAttendee {
  id: string;
  first_name: string;
  last_name: string;
  username?: string;
  email?: string;
  bio?: string;
  profilePictureUrl?: string;
  interests?: Array<{ id: number; name: string; description: string }>;
  preferred_tags?: Array<{ id: number; name: string; created_at: string }>;
  registered_at: string;
  status: 'confirmed' | 'pending' | 'declined' | 'maybe';
  created_at?: string;
  updated_at?: string;
  participation?: {
    registered_at: string;
    status: string;
  };
  other_activities?: Array<{
    id: number;
    title: string;
    date: string;
    status: string;
  }>;
}

// Interface for attendees response
export interface ActivityAttendeesResponse {
  activity_id: number;
  activity_title: string;
  attendees_count: number;
  attendees: ActivityAttendee[];
}

// Interface for detailed attendee response
export interface DetailedAttendeeResponse {
  activity_id: number;
  activity_title: string;
  attendee: ActivityAttendee;
}

// Interface for participant approval/rejection response
export interface ParticipantActionResponse {
  message: string;
  notification_sent: boolean;
  notification_details: {
    success: boolean;
    message_id?: string;
  };
}

// Get all attendees for an activity (including pending requests)
export const fetchActivityAttendees = async (activityId: number): Promise<ActivityAttendeesResponse> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/attendees/`;
  
  console.log('🌐 Attempting API call to:', url);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    console.log('📡 Fetch Attendees Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Fetch attendees error details:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to fetch attendees: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Fetch Attendees Success! Count:', data.attendees_count);
    
    return data;
  } catch (error) {
    console.error('❌ Fetch Attendees Error:', error);
    throw error;
  }
};

// Get detailed attendees list with full profile information
export const fetchDetailedActivityAttendees = async (activityId: number): Promise<ActivityAttendeesResponse> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/detailed_attendees/`;
  
  console.log('🌐 Attempting API call to:', url);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    console.log('📡 Fetch Detailed Attendees Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Fetch detailed attendees error details:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to fetch detailed attendees: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Fetch Detailed Attendees Success! Count:', data.attendees_count);
    
    return data;
  } catch (error) {
    console.error('❌ Fetch Detailed Attendees Error:', error);
    throw error;
  }
};

// Get detailed information about a specific attendee
export const fetchAttendeeDetails = async (activityId: number, userId: string): Promise<DetailedAttendeeResponse> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/attendee/${userId}/`;
  
  console.log('🌐 Attempting API call to:', url);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    console.log('📡 Fetch Attendee Details Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Fetch attendee details error details:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to fetch attendee details: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Fetch Attendee Details Success for user:', userId);
    
    return data;
  } catch (error) {
    console.error('❌ Fetch Attendee Details Error:', error);
    throw error;
  }
};

// Approve a participant request
export const approveParticipantRequest = async (activityId: number, userId: string): Promise<ParticipantActionResponse> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/approve-participant/`;
  
  console.log('🌐 Attempting API call to:', url);
  console.log('📤 Approve participant data:', { user_id: userId });
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ user_id: userId }),
    });

    console.log('📡 Approve Participant Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Approve participant error details:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to approve participant: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Approve Participant Success for user:', userId);
    
    return data;
  } catch (error) {
    console.error('❌ Approve Participant Error:', error);
    throw error;
  }
};

// Reject a participant request
export const rejectParticipantRequest = async (activityId: number, userId: string): Promise<ParticipantActionResponse> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/reject-participant/`;
  
  console.log('🌐 Attempting API call to:', url);
  console.log('📤 Reject participant data:', { user_id: userId });
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ user_id: userId }),
    });

    console.log('📡 Reject Participant Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Reject participant error details:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to reject participant: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Reject Participant Success for user:', userId);
    
    return data;
  } catch (error) {
    console.error('❌ Reject Participant Error:', error);
    throw error;
  }
};

// ============================
// ACTIVITY DETAIL & REGISTRATION APIs
// ============================

// Get detailed information about a specific activity
export const fetchActivityDetails = async (activityId: number): Promise<Activity> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/`;
  
  console.log('🌐 Attempting API call to:', url);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    console.log('📡 Fetch Activity Details Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Fetch activity details error:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to fetch activity details: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Fetch Activity Details Success for activity:', activityId);
    
    return data;
  } catch (error) {
    console.error('❌ Fetch Activity Details Error:', error);
    throw error;
  }
};

export interface ActivityReviewsQueryParams {
  page?: number;
  page_size?: number;
}

const buildActivityReviewsUrl = (activityId: number, params: ActivityReviewsQueryParams = {}): string => {
  const searchParams = new URLSearchParams();

  if (params.page && params.page > 0) {
    searchParams.set('page', params.page.toString());
  }

  if (params.page_size && params.page_size > 0) {
    searchParams.set('page_size', params.page_size.toString());
  }

  const query = searchParams.toString();
  return `${API_BASE_URL}/api/activities/${activityId}/reviews/${query ? `?${query}` : ''}`;
};

export const fetchActivityReviews = async (
  activityId: number,
  params: ActivityReviewsQueryParams = {}
): Promise<ActivityReviewsResponse> => {
  const url = buildActivityReviewsUrl(activityId, params);

  console.log('🌐 Fetching activity reviews:', { url, activityId, params });

  const headers = await getAuthHeaders();

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    console.log('📡 Fetch Activity Reviews Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Fetch activity reviews error:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to fetch activity reviews: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Fetch Activity Reviews Success for activity:', activityId);

    return data;
  } catch (error) {
    console.error('❌ Fetch Activity Reviews Error:', error);
    throw error;
  }
};

export const submitActivityReview = async (
  activityId: number,
  payload: ActivityReviewRequest,
  method: 'POST' | 'PUT' | 'PATCH' = 'POST'
): Promise<ActivityReview> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/reviews/`;

  console.log('🌐 Submitting activity review:', { url, activityId, payload, method });

  const headers = await getAuthHeaders();

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: JSON.stringify(payload),
    });

    console.log('📡 Submit Activity Review Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Submit activity review error:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to submit review: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Submit Activity Review Success for activity:', activityId);

    return data;
  } catch (error) {
    console.error('❌ Submit Activity Review Error:', error);
    throw error;
  }
};

export const deleteActivityReview = async (activityId: number): Promise<void> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/reviews/`;

  console.log('🗑️ Deleting activity review:', { url, activityId });

  const headers = await getAuthHeaders();

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    console.log('📡 Delete Activity Review Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Delete activity review error:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to delete review: ${errorDetails}`);
    }

    console.log('✅ Delete Activity Review Success for activity:', activityId);
  } catch (error) {
    console.error('❌ Delete Activity Review Error:', error);
    throw error;
  }
};

// Register for an activity
export interface ActivityRegistrationResponse {
  message: string;
  status: 'confirmed' | 'pending';
  notification_sent: boolean;
  notification_details?: {
    success: boolean;
    message_id?: string;
  };
}

export const registerForActivity = async (activityId: number): Promise<ActivityRegistrationResponse> => {
  const url = `${API_BASE_URL}/api/activities/${activityId}/register/`;
  
  console.log('🌐 Attempting API call to:', url);
  console.log('📤 Register for activity:', activityId);
  
  const headers = await getAuthHeaders();
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    console.log('📡 Register for Activity Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('❌ Register for activity error details:', errorData);
        errorDetails = errorData.message || errorData.detail || JSON.stringify(errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response');
      }
      throw new Error(`Failed to register for activity: ${errorDetails}`);
    }

    const data = await response.json();
    console.log('✅ Register for Activity Success for activity:', activityId);
    
    return data;
  } catch (error) {
    console.error('❌ Register for Activity Error:', error);
    throw error;
  }
};

// Unregister from an activity
export interface ActivityUnregistrationResponse {
  message: string;
  notification_sent: boolean;
  notification_details?: {
    success: boolean;
    message_id?: string;
  };
}

export const unregisterFromActivity = async (activityId: number): Promise<ActivityUnregistrationResponse> => {
  try {
    console.log(`🔄 Unregistering from activity ${activityId}...`);
    
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/activities/${activityId}/unregister/`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Successfully unregistered from activity ${activityId}`);
    return data;
  } catch (error) {
    console.error('❌ Error unregistering from activity:', error);
    throw error;
  }
};

// ======================
// NOTIFICATION API FUNCTIONS
// ======================

export interface FCMTokenResponse {
  message: string;
  token_registered: boolean;
}

export interface NotificationMarkAsReadResponse {
  message: string;
  notification_id: number;
  marked_as_read: boolean;
}

export interface NotificationBatchActionResponse {
  message: string;
  affected_count: number;
  failed_count: number;
}

// Register FCM token with backend
export const registerFCMToken = async (fcmToken: string): Promise<FCMTokenResponse> => {
  try {
    console.log('🔄 Registering FCM token with backend...');
    
    const headers = await getDualAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/notifications/register-token/`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ fcm_token: fcmToken }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ FCM token registered successfully');
    return data;
  } catch (error) {
    console.error('❌ Error registering FCM token:', error);
    throw error;
  }
};

// Fetch user's in-app notifications
export const fetchNotifications = async (page: number = 1, unreadOnly: boolean = false): Promise<PaginatedNotifications> => {
  try {
    console.log(`🔄 Fetching notifications (page: ${page}, unread only: ${unreadOnly})...`);
    
    const headers = await getDualAuthHeaders();
    const params = new URLSearchParams({
      page: page.toString(),
      ...(unreadOnly && { unread_only: 'true' })
    });
    
    const response = await fetch(`${API_BASE_URL}/api/notifications/?${params}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Fetched ${data.results?.length || 0} notifications`);
    return data;
  } catch (error) {
    console.error('❌ Error fetching notifications:', error);
    throw error;
  }
};

// Mark a notification as read
export const markNotificationAsRead = async (notificationId: number): Promise<NotificationMarkAsReadResponse> => {
  try {
    console.log(`🔄 Marking notification ${notificationId} as read...`);
    
    const headers = await getDualAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/notifications/${notificationId}/mark_as_read/`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Notification ${notificationId} marked as read`);
    return data;
  } catch (error) {
    console.error('❌ Error marking notification as read:', error);
    throw error;
  }
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async (): Promise<NotificationBatchActionResponse> => {
  try {
    console.log('🔄 Marking all notifications as read...');
    
    const headers = await getDualAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/notifications/mark-all-as-read/`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Marked ${data.affected_count} notifications as read`);
    return data;
  } catch (error) {
    console.error('❌ Error marking all notifications as read:', error);
    throw error;
  }
};

// Delete a notification
export const deleteNotification = async (notificationId: number): Promise<{ message: string }> => {
  try {
    console.log(`🔄 Deleting notification ${notificationId}...`);
    
    const headers = await getDualAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/notifications/${notificationId}/`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Notification ${notificationId} deleted`);
    return data;
  } catch (error) {
    console.error('❌ Error deleting notification:', error);
    throw error;
  }
};

// Get notification statistics
export const getNotificationStats = async (): Promise<{
  total_notifications: number;
  unread_count: number;
  today_count: number;
  this_week_count: number;
}> => {
  try {
    console.log('🔄 Fetching notification statistics...');
    
    const headers = await getDualAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/notifications/stats/`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Notification stats: ${data.unread_count} unread, ${data.total_notifications} total`);
    return data;
  } catch (error) {
    console.error('❌ Error fetching notification stats:', error);
    throw error;
  }
};

// Update notification preferences
export const updateNotificationPreferences = async (preferences: NotificationPreferences): Promise<NotificationPreferences> => {
  try {
    console.log('🔄 Updating notification preferences...');
    
    const headers = await getDualAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/notifications/preferences/`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(preferences),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Notification preferences updated');
    return data;
  } catch (error) {
    console.error('❌ Error updating notification preferences:', error);
    throw error;
  }
};

// Fetch notification preferences
export const fetchNotificationPreferences = async (): Promise<NotificationPreferences> => {
  try {
    console.log('🔄 Fetching notification preferences...');
    
    const headers = await getDualAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/notifications/preferences/`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Notification preferences fetched');
    return data;
  } catch (error) {
    console.error('❌ Error fetching notification preferences:', error);
    throw error;
  }
};

// Terms & Conditions API Functions

// Fetch Terms & Conditions by language
export const fetchTermsAndConditions = async (language: 'EN' | 'DE' = 'EN', version?: string): Promise<TermsAndConditions> => {
  console.log('🔍 Fetching Terms & Conditions', { language, version });

  try {
    const params = new URLSearchParams({ language });
    if (version) params.append('version', version);
    
    const url = `${API_BASE_URL}/api/terms-and-conditions/?${params}`;
    console.log('🌐 API call to:', url);

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Terms & Conditions fetched successfully');
    return result.data;
  } catch (error) {
    console.error('❌ Error fetching Terms & Conditions:', error);
    throw error;
  }
};

// Privacy Policy API
export interface PrivacyPolicy {
  id: number;
  language: 'EN' | 'DE';
  version: string;
  title: string;
  content: string;
  effective_date: string;
  is_active: boolean;
  created_at: string;
}

export const fetchPrivacyPolicy = async (language: 'EN' | 'DE' = 'EN', version?: string): Promise<PrivacyPolicy> => {
  console.log('🔍 Fetching Privacy Policy', { language, version });
  try {
    const params = new URLSearchParams({ language });
    if (version) params.append('version', version);

    const url = `${API_BASE_URL}/api/legal/privacy/?${params}`;
    console.log('🌐 API call to:', url);

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const result = await response.json();
    console.log('✅ Privacy Policy fetched successfully');
    return result;
  } catch (error) {
    console.error('❌ Error fetching Privacy Policy:', error);
    throw error;
  }
};

// ------------------------------
// Public Site Info / About / Contact Support
// ------------------------------

export interface SiteInfo {
  support_email?: string;
  support_hours_weekdays?: string;
  support_hours_weekends?: string;
  support_sla_summary?: string;
  social?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    youtube?: string;
    linkedin?: string;
  };
  faq?: Array<{ title: string; url: string }>;
  logo_url?: string;
}

export interface ContactSupportRequest {
  name: string;
  email: string;
  topic: string;
  message: string;
}

export interface ContactSupportResponse {
  message: string;
  ticket_id?: string;
}

// New About response shape from backend
export interface AboutResponse {
  content: {
    language: 'EN' | 'DE';
    mission_text?: string | null;
    how_it_works?: Array<unknown>;
    for_participants_text?: string | null;
    for_organizers_text?: string | null;
    trust_cities_text?: string | null;
    trust_ratings_text?: string | null;
    hero_image_url?: string | null;
    section_image_urls?: string[];
    is_active?: boolean;
    updated_at?: string;

    impressum_company_name?: string | null;
    impressum_address?: string | null;
    impressum_email?: string | null;
    impressum_phone?: string | null;
    impressum_responsible_person?: string | null;
    impressum_commercial_register?: string | null;
    impressum_vat_id?: string | null;
  };
  trust_signals?: {
    cities?: number;
    organizers?: number;
    avg_rating?: number;
  };
  site?: {
    support_email?: string | null;
    support_hours_en?: string | null;
    support_hours_de?: string | null;
    support_sla_en?: string | null;
    support_sla_de?: string | null;
    faq_url_en?: string | null;
    faq_url_de?: string | null;
    social_instagram_url?: string | null;
    social_facebook_url?: string | null;
    logo_url?: string | null;
    updated_at?: string | null;
  };
}

export const fetchPublicSiteInfo = async (): Promise<SiteInfo> => {
  try {
    const url = `${API_BASE_URL}/api/public/site-info/`;
    const headers = getPublicHeaders();
    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('❌ Public Site Info Error:', error);
    throw error;
  }
};

export const postSupportContact = async (payload: ContactSupportRequest): Promise<ContactSupportResponse> => {
  try {
    const url = `${API_BASE_URL}/api/support/contact/`;
    const headers = getPublicHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('❌ Support Contact Error:', error);
    throw error;
  }
};

export const fetchPublicAbout = async (language: 'EN' | 'DE' = 'EN'): Promise<AboutResponse> => {
  try {
    const params = new URLSearchParams({ language });
    const url = `${API_BASE_URL}/api/public/about/?${params.toString()}`;
    const headers = getPublicHeaders();
    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('❌ Public About Error:', error);
    throw error;
  }
};

// Accept Terms & Conditions
export const acceptTermsAndConditions = async (language: 'EN' | 'DE', termsVersion?: string): Promise<TermsAcceptance> => {
  console.log('🔍 Accepting Terms & Conditions', { language, termsVersion });

  try {
    const headers = await getAuthHeaders();
    const url = `${API_BASE_URL}/api/terms/accept/`;
    
    const body: { language: 'EN' | 'DE'; terms_version?: string } = { language };
    if (termsVersion) body.terms_version = termsVersion;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Terms & Conditions accepted successfully');
    return result.data;
  } catch (error) {
    console.error('❌ Error accepting Terms & Conditions:', error);
    throw error;
  }
};

// Check user's Terms & Conditions status
export const fetchUserTermsStatus = async (language: 'EN' | 'DE' = 'EN'): Promise<TermsStatus> => {
  console.log('🔍 Fetching user Terms status', { language });

  try {
    const headers = await getAuthHeaders();
    const url = `${API_BASE_URL}/api/user/terms-status/?language=${language}`;
    
    const response = await fetch(url, { headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ User Terms status fetched successfully');
    return result.data;
  } catch (error) {
    console.error('❌ Error fetching user Terms status:', error);
    throw error;
  }
};

// ===== RATING SYSTEM API =====

export interface OrganizerRating {
  id: number;
  organizer: number;
  organizer_name: string;
  rater: number;
  rater_name: string;
  activity: number;
  activity_title: string;
  rating: number;
  comment: string;
  created_at: string;
}

export interface RateOrganizerRequest {
  rating: number; // 1-5
  comment?: string;
}

export interface RateOrganizerResponse {
  id: number;
  organizer: number;
  organizer_name: string;
  rater: number;
  rater_name: string;
  activity: number;
  activity_title: string;
  rating: number;
  comment: string;
  created_at: string;
}

/**
 * Rate an organizer for a specific activity
 * @param activityId The activity ID
 * @param ratingData Rating data (rating 1-5, optional comment)
 */
export const rateOrganizer = async (
  activityId: number,
  ratingData: RateOrganizerRequest
): Promise<RateOrganizerResponse> => {
  console.log('🌟 Rating organizer for activity', { activityId, ratingData });

  try {
    const headers = await getAuthHeaders();
    const url = `${API_BASE_URL}/api/activities/${activityId}/rate_organizer/`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(ratingData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.error || errorData.detail || errorData.message || `Failed to rate organizer: ${response.status}`;
      console.error('❌ Error rating organizer:', errorMessage);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('✅ Successfully rated organizer', result);
    return result;
  } catch (error) {
    console.error('❌ Error rating organizer:', error);
    throw error;
  }
};

// ===== ORGANIZER DETAIL API =====

export interface OrganizerDetailProfile {
  userId: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  bio: string;
  profilePictureUrl: string;
  interests: number[];
  interests_detail: Array<{
    id: number;
    name: string;
    description: string;
  }>;
  preferred_tags: number[];
  preferred_tags_detail: Array<{
    id: number;
    name: string;
  }>;
  city: string;
  country: string;
  terms_accepted: boolean;
  terms_accepted_at: string;
  terms_version: string;
  has_accepted_current_terms: boolean;
  gallery?: GalleryMediaItem[];
  galleryPreview?: GalleryMediaItem[];
  galleryCount?: number;
  galleryLimit?: number;
}

/**
 * Fetch detailed organizer profile by firebase UID
 * @param firebaseUid The firebase UID of the organizer
 */
export const fetchOrganizerDetailProfile = async (firebaseUid: string): Promise<OrganizerDetailProfile> => {
  console.log('👤 Fetching organizer detail profile for:', firebaseUid);

  try {
    const headers = await getAuthHeaders();
    const url = `${API_BASE_URL}/api/users/${firebaseUid}/`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.error || errorData.detail || errorData.message || `Failed to fetch organizer profile: ${response.status}`;
      console.error('❌ Error fetching organizer profile:', errorMessage);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('✅ Successfully fetched organizer profile for:', firebaseUid);
    return result;
  } catch (error) {
    console.error('❌ Error fetching organizer profile:', error);
    throw error;
  }
};

/**
 * Fetch activities created by a specific organizer
 * @param organizerId The firebase UID of the organizer
 * @param page Page number for pagination (default: 1)
 * @param dateFrom Optional start date filter (YYYY-MM-DD)
 * @param dateTo Optional end date filter (YYYY-MM-DD)
 */
export const fetchActivitiesByOrganizer = async (
  organizerId: string,
  page: number = 1,
  dateFrom?: string,
  dateTo?: string
): Promise<PaginatedActivities> => {
  console.log('📅 Fetching activities by organizer:', { organizerId, page, dateFrom, dateTo });

  try {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    
    params.append('creator', organizerId);
    params.append('page', page.toString());
    
    if (dateFrom) {
      params.append('dateFrom', dateFrom);
    }
    if (dateTo) {
      params.append('dateTo', dateTo);
    }
    
    const url = `${API_BASE_URL}/api/activities/?${params.toString()}`;
    console.log('🌐 API call to:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch organizer activities: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Successfully fetched organizer activities:', result.count, 'total activities');
    return result;
  } catch (error) {
    console.error('❌ Error fetching organizer activities:', error);
    throw error;
  }
};

export interface OrganizerRatingsResponse {
  organizer: {
    userId: string;
    id: string;
    name: string;
    username: string;
    email: string;
    profilePictureUrl: string;
    bio: string;
    city: string;
    country: string;
  };
  ratingBreakdown: {
    fiveStars: number;
    fourStars: number;
    threeStars: number;
    twoStars: number;
    oneStar: number;
  };
  recentRatings: Array<{
    id: number;
    rater_name: string;
    rater_email: string;
    activity_title: string;
    activity_id: number;
    rating: number;
    comment: string;
    created_at: string;
  }>;
  pagination: {
    page: number;
    page_size: number;
    total_ratings: number;
    total_pages: number;
  };
}

/**
 * Fetch ratings for a specific organizer
 * @param firebaseUid The firebase UID of the organizer
 */
export const fetchOrganizerRatings = async (firebaseUid: string): Promise<OrganizerRatingsResponse> => {
  console.log('⭐ Fetching organizer ratings for:', firebaseUid);

  try {
    const headers = await getAuthHeaders();
    const url = `${API_BASE_URL}/api/users/${firebaseUid}/ratings/`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.error || errorData.detail || errorData.message || `Failed to fetch organizer ratings: ${response.status}`;
      console.error('❌ Error fetching organizer ratings:', errorMessage);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('✅ Successfully fetched organizer ratings:', result.total_ratings, 'total ratings');
    return result;
  } catch (error) {
    console.error('❌ Error fetching organizer ratings:', error);
    throw error;
  }
};

// Pricing and Subscription Types

export interface TrialInfo {
  credit_amount: number;
  activity_cost: number;
  trial_activities: number;
  trial_duration_days: number;
}

export interface PricingResponse {
  plans: SubscriptionPlan[];
  trial_info: TrialInfo;
  currency: string;
  features_comparison: Record<string, string[]>;
  // Optional global free-access flags
  pricing_hidden?: boolean;
  message?: string;
  alternative_message?: string;
  free_until?: string;
  days_remaining?: number;
}

/**
 * Fetch all available subscription plans and trial information
 * Public endpoint - no authentication required
 */
export const fetchPricingPlans = async (): Promise<PricingResponse> => {
  console.log('💰 Fetching pricing plans from:', `${API_BASE_URL}/api/pricing/`);
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/pricing/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': import.meta.env.VITE_API_KEY || '',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || errorData.detail || errorData.message || `Failed to fetch pricing plans: ${response.status}`;
      console.error('❌ Error fetching pricing plans:', errorMessage);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('✅ Successfully fetched pricing plans:', result.plans?.length || 0, 'plans available');
    console.log('📊 Trial info:', `${result.trial_info?.trial_activities || 0} activities for ${result.trial_info?.trial_duration_days || 0} days`);
    return result;
  } catch (error) {
    console.error('❌ Error fetching pricing plans:', error);
    throw error;
  }
};

/**
 * Subscription Management APIs
 */

export interface SubscriptionActivationRequest {
  plan_name: string;
  payment_reference: string;
  payment_method: 'credit_card' | 'paypal' | 'bank_transfer';
}

export interface SubscriptionActivationResponse {
  success: boolean;
  message: string;
  subscription: {
    plan: string;
    plan_display_name: string;
    start_date: string;
    end_date: string;
    amount_paid: number;
    activity_limit: number;
    billing_cycle: 'monthly' | 'yearly';
  };
  subscription_status: {
    status: string;
    plan: string;
    credit_balance: number | null;
    expires_at: string;
    activities_remaining: number;
  };
}

export interface SubscriptionStatusResponse {
  status: 'trial' | 'subscribed' | 'expired' | 'cancelled';
  plan: string | null;
  credit_balance: number | null;
  expires_at: string | null;
  activities_remaining: number;
  billing_period_start: string | null;
  activities_created_this_period: number;
  trial_end_date: string | null;
  can_create_activity: boolean;
  plan_limit?: number;
}

// New Activity Creation Validation Endpoint
export interface ActivityCreationValidationResponse {
  can_create: boolean;
  subscription: {
    type: string;
    status: string;
    activities_remaining: number;
    credit_balance: number;
  };
  reason: string | null;
  message: string;
  cost?: number;
  remaining_after_creation?: {
    activities: number;
    credits: number;
  };
  required_credits?: number;
  current_credits?: number;
  credit_deficit?: number;
  required_action?: string;
  suggested_actions?: Array<{
    action: string;
    label: string;
    url: string;
    primary: boolean;
  }>;
}

// Enhanced Activity Creation Error Response
export interface ActivityCreationErrorResponse {
  error: string;
  message: string;
  required_credits?: number;
  current_credits?: number;
  credit_deficit?: number;
  subscription: {
    type: string;
    status: string;
    credit_balance: number;
    activities_remaining: number;
  };
  suggested_actions: Array<{
    action: string;
    label: string;
    url: string;
    primary: boolean;
  }>;
}

// Custom error class for subscription-related activity creation failures
export class ActivityCreationError extends Error {
  public errorData: ActivityCreationErrorResponse;
  public isSubscriptionError: boolean = true;

  constructor(message: string, errorData: ActivityCreationErrorResponse) {
    super(message);
    this.name = 'ActivityCreationError';
    this.errorData = errorData;
  }
}

/**
 * Activate a subscription plan for the authenticated user
 * Requires authentication
 */
export const activateSubscription = async (subscriptionData: SubscriptionActivationRequest): Promise<SubscriptionActivationResponse> => {
  console.log('💳 Activating subscription:', subscriptionData.plan_name);
  
  try {
    const url = `${API_BASE_URL}/api/subscription/activate/`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscriptionData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || errorData.detail || errorData.message || `Failed to activate subscription: ${response.status}`;
      console.error('❌ Error activating subscription:', errorMessage);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('✅ Successfully activated subscription:', result.subscription.plan_display_name);
    return result;
  } catch (error) {
    console.error('❌ Error activating subscription:', error);
    throw error;
  }
};

/**
 * Cancel the current subscription for the authenticated user
 * Requires authentication
 */
export const cancelSubscription = async (): Promise<{ success: boolean; message: string; subscription_status: SubscriptionStatusResponse }> => {
  console.log('🚫 Cancelling subscription');
  
  try {
    const url = `${API_BASE_URL}/api/subscription/cancel/`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || errorData.detail || errorData.message || `Failed to cancel subscription: ${response.status}`;
      console.error('❌ Error cancelling subscription:', errorMessage);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('✅ Successfully cancelled subscription');
    return result;
  } catch (error) {
    console.error('❌ Error cancelling subscription:', error);
    throw error;
  }
};

/**
 * Get current user's complete subscription information
 * Requires authentication
 */
export const fetchSubscriptionStatus = async (): Promise<SubscriptionStatusResponse> => {
  console.log('📊 Fetching subscription status');
  
  try {
    const url = `${API_BASE_URL}/api/subscription/status/`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || errorData.detail || errorData.message || `Failed to fetch subscription status: ${response.status}`;
      console.error('❌ Error fetching subscription status:', errorMessage);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('✅ Successfully fetched subscription status:', result.status);
    return result;
  } catch (error) {
    console.error('❌ Error fetching subscription status:', error);
    throw error;
  }
};

// Subscription History
export interface SubscriptionHistoryEntry {
  id: number;
  user: string;
  user_name: string;
  plan: number;
  plan_name: string;
  status: 'active' | 'expired' | 'cancelled' | 'pending';
  start_date: string;
  end_date: string;
  amount_paid: number;
  payment_method: 'credit_card' | 'paypal' | 'bank_transfer';
  payment_reference: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export type SubscriptionHistory = SubscriptionHistoryEntry[];

/**
 * Fetch user's subscription history
 * Requires authentication
 */
export const fetchSubscriptionHistory = async (): Promise<SubscriptionHistory> => {
  console.log('📊 Fetching subscription history');
  
  try {
    const url = `${API_BASE_URL}/api/subscription-history/`;
    console.log('🌐 Attempting API call to:', url);
    
    const headers = await getAuthHeaders();
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || errorData.detail || errorData.message || `Failed to fetch subscription history: ${response.status}`;
      console.error('❌ Error fetching subscription history:', errorMessage);
      throw new Error(errorMessage);
    }

    const result: SubscriptionHistory = await response.json();
    console.log('✅ Successfully fetched subscription history:', result.length, 'entries');
    return result;
  } catch (error) {
    console.error('❌ Error fetching subscription history:', error);
    throw error;
  }
};

/**
 * Validate if user can create an activity before showing creation form
 * Provides detailed validation with suggested actions
 */
export const validateActivityCreation = async (): Promise<ActivityCreationValidationResponse> => {
  console.log('🔍 Validating activity creation permissions');
  try {
    const url = `${API_BASE_URL}/api/activities/validate-creation/`;
    const headers = await getAuthHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || errorData.detail || errorData.message || `Failed to validate activity creation: ${response.status}`;
      console.error('❌ Error validating activity creation:', errorMessage);
      throw new Error(errorMessage);
    }

    const result: ActivityCreationValidationResponse = await response.json();
    console.log('✅ Activity creation validation result:', {
      can_create: result.can_create,
      reason: result.reason,
      activities_remaining: result.subscription.activities_remaining,
      credit_balance: result.subscription.credit_balance
    });
    return result;
  } catch (error) {
    console.error('❌ Error validating activity creation:', error);
    throw error;
  }
};

// ==============================================
// Cities Discovery API
// ==============================================

// Country interface for /api/countries/ endpoint (matches backend documentation)
export interface Country {
  id: number;
  code: string;
  name: string;
  image_url: string | null;
  computed_image_url: string; // Has Unsplash fallback
  thumbnail_url: string | null;
  description: string | null;
  is_featured: boolean;
  is_active: boolean;
  activity_count: number;
  city_count: number;
  last_activity_date: string | null;
  created_at: string;
  updated_at: string;
}

// City interface for /api/cities/ endpoint (matches backend documentation)
export interface City {
  id: number;
  name: string;
  country: string;
  display_name: string;
  full_name: string;
  latitude: string | null;
  longitude: string | null;
  image_url: string | null;
  computed_image_url: string; // Has Unsplash fallback
  thumbnail_url: string | null;
  description: string | null;
  is_featured: boolean;
  is_active: boolean;
  activity_count: number;
  last_activity_date: string | null;
  created_at: string;
  updated_at: string;
}

// City interface for /api/activities/cities/ endpoint (legacy, used in horizontal lists)
export interface CityWithActivities {
  id?: number; // City ID for API operations
  name: string;
  country: string;
  activity_count: number;
  image_url: string;
  thumbnail_url?: string | null; // Thumbnail URL for city list display
  latitude?: number;
  longitude?: number;
}

export interface CitiesResponse {
  country: string;
  cities: CityWithActivities[];
  total_cities: number;
}

export interface CityActivitiesResponse {
  city: string;
  country: string;
  results_count: number;
  activities: Activity[];
}

export interface CountryWithActivities {
  country_code: string;
  country_name: string;
  activity_count: number;
  thumbnail_url?: string | null;
  image_url?: string | null;
}

export interface CountriesResponse {
  countries: CountryWithActivities[];
  total_countries: number;
}

/**
 * Fetch cities with activities in user's country or specified country
 */
export const fetchCitiesWithActivities = async (country?: string): Promise<CitiesResponse> => {
  console.log('🏙️ Fetching cities with activities', { country });
  try {
    const headers = await getAuthHeaders();
    
    let url = `${API_BASE_URL}/api/activities/cities/`;
    if (country) {
      url += `?country=${encodeURIComponent(country)}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch cities:', errorText);
      throw new Error(`Failed to fetch cities: ${response.status} ${response.statusText}`);
    }

    const result = await response.json() as CitiesResponse;
    console.log('✅ Cities with activities loaded:', {
      country: result.country,
      total_cities: result.total_cities,
      cities_count: result.cities.length
    });
    
    return result;
  } catch (error) {
    console.error('❌ Error fetching cities with activities:', error);
    throw error;
  }
};

/**
 * Fetch countries with activities
 * Optional authentication may show more countries
 */
export const fetchCountriesWithActivities = async (): Promise<CountriesResponse> => {
  console.log('🌍 Fetching countries with activities');
  try {
    const headers = await getAuthHeaders();
    
    const url = `${API_BASE_URL}/api/activities/countries/`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch countries:', errorText);
      throw new Error(`Failed to fetch countries: ${response.status} ${response.statusText}`);
    }

    const result = await response.json() as CountriesResponse;
    console.log('✅ Countries with activities loaded:', {
      total_countries: result.total_countries,
      countries_count: result.countries.length
    });
    
    return result;
  } catch (error) {
    console.error('❌ Error fetching countries with activities:', error);
    throw error;
  }
};

/**
 * Fetch activities in a specific city
 */
export const fetchActivitiesByCity = async (city: string, country: string): Promise<CityActivitiesResponse> => {
  console.log('🏙️ Fetching activities by city', { city, country });
  try {
    const headers = await getAuthHeaders();
    
    const url = `${API_BASE_URL}/api/activities/by_city/?city=${encodeURIComponent(city)}&country=${encodeURIComponent(country)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch city activities:', errorText);
      throw new Error(`Failed to fetch city activities: ${response.status} ${response.statusText}`);
    }

    const result = await response.json() as CityActivitiesResponse;
    console.log('✅ City activities loaded:', {
      city: result.city,
      country: result.country,
      results_count: result.results_count,
      activities_count: result.activities.length
    });
    
    return result;
  } catch (error) {
    console.error('❌ Error fetching activities by city:', error);
    throw error;
  }
};

/**
 * Fetch all countries (for team member country image management)
 * GET /api/countries/
 * Returns array of Country objects matching backend documentation
 */
export const fetchAllCountries = async (): Promise<Country[]> => {
  console.log('🌍 Fetching all countries for management');
  try {
    const headers = await getAuthHeaders();
    
    const url = `${API_BASE_URL}/api/countries/`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch all countries:', errorText);
      throw new Error(`Failed to fetch all countries: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('🔍 Raw response from /api/countries/:', {
      result,
      isArray: Array.isArray(result),
      type: typeof result,
      keys: result && typeof result === 'object' ? Object.keys(result) : null
    });
    
    // Handle both array response and paginated response
    let countries: Country[] = [];
    if (Array.isArray(result)) {
      countries = result;
    } else if (result && typeof result === 'object') {
      // Handle paginated response or nested structure
      countries = result.results || result.countries || result.data || [];
    }
    
    console.log('✅ All countries loaded:', {
      total: countries.length,
      countriesWithIds: countries.filter(c => c.id !== undefined && c.id !== null).length,
      sample: countries.length > 0 ? countries[0] : null
    });
    
    return countries as Country[];
  } catch (error) {
    console.error('❌ Error fetching all countries:', error);
    throw error;
  }
};

/**
 * Fetch all cities (for team member city image management)
 * GET /api/cities/
 * Returns array of City objects matching backend documentation
 */
export const fetchAllCities = async (): Promise<City[]> => {
  console.log('🏙️ Fetching all cities for management');
  try {
    const headers = await getAuthHeaders();
    
    const url = `${API_BASE_URL}/api/cities/`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch all cities:', errorText);
      throw new Error(`Failed to fetch all cities: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('🔍 Raw response from /api/cities/:', {
      result,
      isArray: Array.isArray(result),
      type: typeof result,
      keys: result && typeof result === 'object' ? Object.keys(result) : null
    });
    
    // Handle both array response and paginated response
    let cities: City[] = [];
    if (Array.isArray(result)) {
      cities = result;
    } else if (result && typeof result === 'object') {
      // Handle paginated response or nested structure
      cities = result.results || result.cities || result.data || [];
    }
    
    console.log('✅ All cities loaded:', {
      total: cities.length,
      citiesWithIds: cities.filter(c => c.id !== undefined && c.id !== null).length,
      sample: cities.length > 0 ? cities[0] : null
    });
    
    return cities as City[];
  } catch (error) {
    console.error('❌ Error fetching all cities:', error);
    throw error;
  }
};

/**
 * Update country image URLs (Team Members only)
 * PATCH /api/countries/{id}/
 * At least ONE of image_url or thumbnail_url must be provided
 */
export interface UpdateCountryImageRequest {
  image_url?: string | null;
  thumbnail_url?: string | null;
}

export const updateCountryImage = async (
  countryId: number,
  imageData: UpdateCountryImageRequest
): Promise<Country> => {
  console.log('🖼️ Updating country image', { countryId, imageData });
  try {
    const headers = await getAuthHeaders();
    
    // Build request body - at least one field must be provided
    const body: { image_url?: string | null; thumbnail_url?: string | null } = {};
    if (imageData.image_url !== undefined) body.image_url = imageData.image_url;
    if (imageData.thumbnail_url !== undefined) body.thumbnail_url = imageData.thumbnail_url;

    const url = `${API_BASE_URL}/api/countries/${countryId}/`;

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to update country image:', errorText);
      throw new Error(`Failed to update country image: ${response.status} ${response.statusText}`);
    }

    const result = await response.json() as Country;
    console.log('✅ Country image updated successfully:', result.name);
    return result;
  } catch (error) {
    console.error('❌ Error updating country image:', error);
    throw error;
  }
};

/**
 * Update city image URLs (Team Members only)
 * PATCH /api/cities/{id}/
 * At least ONE of image_url or thumbnail_url must be provided
 */
export interface UpdateCityImageRequest {
  image_url?: string | null;
  thumbnail_url?: string | null;
}

export const updateCityImage = async (
  cityId: number,
  imageData: UpdateCityImageRequest
): Promise<City> => {
  console.log('🖼️ Updating city image', { cityId, imageData });
  try {
    const headers = await getAuthHeaders();
    
    // Build request body - at least one field must be provided
    const body: { image_url?: string | null; thumbnail_url?: string | null } = {};
    if (imageData.image_url !== undefined) body.image_url = imageData.image_url;
    if (imageData.thumbnail_url !== undefined) body.thumbnail_url = imageData.thumbnail_url;

    const url = `${API_BASE_URL}/api/cities/${cityId}/`;

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to update city image:', errorText);
      throw new Error(`Failed to update city image: ${response.status} ${response.statusText}`);
    }

    const result = await response.json() as City;
    console.log('✅ City image updated successfully:', result.name);
    return result;
  } catch (error) {
    console.error('❌ Error updating city image:', error);
    throw error;
  }
}; 